import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useAuth } from '../hooks';
import { 
  createAiDebate, 
  getTopics, 
  getOpenLobbies, 
  getMyActiveMatches, 
  deleteDebate, 
  joinPrivateGame, 
  generateTrainingTopic,
  createTemporaryTopic,
  saveUserPreference,
  getArticleContent,
  getHotDebates,
  getLocalNews,
  updateUserProfile
} from '../api';
import { useData } from '../hooks';
import {
  FireIcon, TrophyIcon, BoltIcon, FlagIcon, ArrowRightIcon, UserGroupIcon, ClockIcon,
  CpuChipIcon, BuildingLibraryIcon, GlobeAmericasIcon, CurrencyDollarIcon, BeakerIcon,
  NewspaperIcon, FilmIcon, HeartIcon, ScaleIcon, XMarkIcon, LockClosedIcon,
  ArrowPathIcon, PlusIcon, SparklesIcon, AcademicCapIcon, SpeakerWaveIcon,
  ChatBubbleOvalLeftIcon, GiftIcon, LinkIcon, MagnifyingGlassIcon, ShieldCheckIcon,
  CheckIcon, ArrowLeftIcon, StarIcon, PlayCircleIcon, UsersIcon, TrashIcon,
  MapPinIcon, GlobeAltIcon, KeyIcon, ArchiveBoxIcon, ChatBubbleBottomCenterTextIcon,
  EyeSlashIcon, ArrowDownCircleIcon, Squares2X2Icon, FilmIcon as ReelIcon 
} from '@heroicons/react/24/solid';
import { MapPinIcon as PinOutline, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import NewsReel from './NewsReel'; // IMPORTED NEWS REEL
import { useConfirm } from '../components/ConfirmModal';
import { useToast } from '../components/Toast';

// --- CONSTANTS ---

const CATEGORIES = [
  { id: 'General', label: 'General', icon: FireIcon, color: 'text-orange-500', type: 'category' },
  { id: 'Tech', label: 'Tech', icon: CpuChipIcon, color: 'text-blue-500', type: 'category' },
  { id: 'Politics', label: 'Politics', icon: BuildingLibraryIcon, color: 'text-red-500', type: 'category' },
  { id: 'Environment', label: 'Nature', icon: GlobeAmericasIcon, color: 'text-green-500', type: 'category' },
  { id: 'Economics', label: 'Money', icon: CurrencyDollarIcon, color: 'text-emerald-500', type: 'category' },
  { id: 'Science', label: 'Science', icon: BeakerIcon, color: 'text-purple-500', type: 'category' },
  { id: 'Media', label: 'News', icon: NewspaperIcon, color: 'text-slate-500', type: 'category' },
  { id: 'Culture', label: 'Culture', icon: FilmIcon, color: 'text-pink-500', type: 'category' },
  { id: 'Sports', label: 'Sports', icon: TrophyIcon, color: 'text-yellow-500', type: 'category' },
  { id: 'Healthcare', label: 'Health', icon: HeartIcon, color: 'text-rose-500', type: 'category' },
  { id: 'Law', label: 'Law', icon: ScaleIcon, color: 'text-indigo-500', type: 'category' },
];

// Consolidated Regions List
const ALL_REGIONS = [
  // US
  { id: 'AL', label: 'Alabama', country: 'US' }, { id: 'AK', label: 'Alaska', country: 'US' }, { id: 'AZ', label: 'Arizona', country: 'US' },
  { id: 'AR', label: 'Arkansas', country: 'US' }, { id: 'CA', label: 'California', country: 'US' }, { id: 'CO', label: 'Colorado', country: 'US' },
  { id: 'CT', label: 'Connecticut', country: 'US' }, { id: 'DE', label: 'Delaware', country: 'US' }, { id: 'FL', label: 'Florida', country: 'US' },
  { id: 'GA', label: 'Georgia', country: 'US' }, { id: 'HI', label: 'Hawaii', country: 'US' }, { id: 'ID', label: 'Idaho', country: 'US' },
  { id: 'IL', label: 'Illinois', country: 'US' }, { id: 'IN', label: 'Indiana', country: 'US' }, { id: 'IA', label: 'Iowa', country: 'US' },
  { id: 'KS', label: 'Kansas', country: 'US' }, { id: 'KY', label: 'Kentucky', country: 'US' }, { id: 'LA', label: 'Louisiana', country: 'US' },
  { id: 'ME', label: 'Maine', country: 'US' }, { id: 'MD', label: 'Maryland', country: 'US' }, { id: 'MA', label: 'Massachusetts', country: 'US' },
  { id: 'MI', label: 'Michigan', country: 'US' }, { id: 'MN', label: 'Minnesota', country: 'US' }, { id: 'MS', label: 'Mississippi', country: 'US' },
  { id: 'MO', label: 'Missouri', country: 'US' }, { id: 'MT', label: 'Montana', country: 'US' }, { id: 'NE', label: 'Nebraska', country: 'US' },
  { id: 'NV', label: 'Nevada', country: 'US' }, { id: 'NH', label: 'New Hampshire', country: 'US' }, { id: 'NJ', label: 'New Jersey', country: 'US' },
  { id: 'NM', label: 'New Mexico', country: 'US' }, { id: 'NY', label: 'New York', country: 'US' }, { id: 'NC', label: 'North Carolina', country: 'US' },
  { id: 'ND', label: 'North Dakota', country: 'US' }, { id: 'OH', label: 'Ohio', country: 'US' }, { id: 'OK', label: 'Oklahoma', country: 'US' },
  { id: 'OR', label: 'Oregon', country: 'US' }, { id: 'PA', label: 'Pennsylvania', country: 'US' }, { id: 'RI', label: 'Rhode Island', country: 'US' },
  { id: 'SC', label: 'South Carolina', country: 'US' }, { id: 'SD', label: 'South Dakota', country: 'US' }, { id: 'TN', label: 'Tennessee', country: 'US' },
  { id: 'TX', label: 'Texas', country: 'US' }, { id: 'UT', label: 'Utah', country: 'US' }, { id: 'VT', label: 'Vermont', country: 'US' },
  { id: 'VA', label: 'Virginia', country: 'US' }, { id: 'WA', label: 'Washington', country: 'US' }, { id: 'WV', label: 'West Virginia', country: 'US' },
  { id: 'WI', label: 'Wisconsin', country: 'US' }, { id: 'WY', label: 'Wyoming', country: 'US' },
  // Canada
  { id: 'AB', label: 'Alberta', country: 'CA' }, { id: 'BC', label: 'British Columbia', country: 'CA' }, { id: 'MB', label: 'Manitoba', country: 'CA' },
  { id: 'NB', label: 'New Brunswick', country: 'CA' }, { id: 'NL', label: 'Newfoundland', country: 'CA' }, { id: 'NS', label: 'Nova Scotia', country: 'CA' },
  { id: 'ON', label: 'Ontario', country: 'CA' }, { id: 'PE', label: 'PEI', country: 'CA' }, { id: 'QC', label: 'Quebec', country: 'CA' },
  { id: 'SK', label: 'Saskatchewan', country: 'CA' },
  // UK
  { id: 'ENG', label: 'England', country: 'UK' }, { id: 'SCT', label: 'Scotland', country: 'UK' }, { id: 'WLS', label: 'Wales', country: 'UK' }, { id: 'NIR', label: 'Northern Ireland', country: 'UK' },
  // Australia
  { id: 'NSW', label: 'New South Wales', country: 'AU' }, { id: 'VIC', label: 'Victoria', country: 'AU' }, { id: 'QLD', label: 'Queensland', country: 'AU' },
  { id: 'WA', label: 'Western Australia', country: 'AU' }, { id: 'SA', label: 'South Australia', country: 'AU' }, { id: 'TAS', label: 'Tasmania', country: 'AU' },
  { id: 'ACT', label: 'ACT', country: 'AU' }, { id: 'NT', label: 'Northern Territory', country: 'AU' }
].map(r => ({ ...r, type: 'region', icon: MapPinIcon, color: 'text-slate-400' }));


// --- DEDICATED COUNTDOWN COMPONENT ---
const Countdown = ({ endsAt }) => {
  const calculateTimeLeft = () => {
    const endDate = endsAt?.toDate ? endsAt.toDate() : new Date(endsAt);
    const now = new Date();
    const difference = endDate - now;
    if (difference <= 0) return null;

    const hours = Math.floor((difference / (1000 * 60 * 60)));
    const minutes = Math.floor((difference / 1000 / 60) % 60);
    const seconds = Math.floor((difference / 1000) % 60);

    return {
      hours: String(hours).padStart(2, '0'),
      minutes: String(minutes).padStart(2, '0'),
      seconds: String(seconds).padStart(2, '0'),
      raw: difference
    };
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  useEffect(() => {
    const timer = setInterval(() => {
      const newTime = calculateTimeLeft();
      if (!newTime) clearInterval(timer);
      setTimeLeft(newTime);
    }, 1000);
    return () => clearInterval(timer);
  }, [endsAt]);

  if (!timeLeft) {
    return (
      <span className="ml-2 text-[8px] md:text-[10px] font-bold px-2 py-1 rounded-[2px] flex items-center font-mono uppercase tracking-wider bg-edge text-ash">
        <ClockIcon className="w-3 h-3 mr-1" /> ENDED
      </span>
    );
  }

  const isUrgent = timeLeft.raw < (1000 * 60 * 60);
  return (
    <span className={`ml-2 text-[8px] md:text-[10px] font-bold px-2 py-1 rounded-[2px] flex items-center font-mono transition-colors ${isUrgent ? 'bg-signal/15 text-signal' : 'bg-edge text-ash'}`}>
      <ClockIcon className="w-3 h-3 mr-1.5" />
      {timeLeft.hours}:{timeLeft.minutes}:{timeLeft.seconds}
    </span>
  );
};

// Designed cover for topics with no image (evergreen/timeless + any imageless story). Deterministic
// dark, category-tinted gradient + a big watermark icon → looks intentional, never broken.
const CATEGORY_COVER = {
  Politics: ['#2a1416', '#100E08'], Tech: ['#0f1e2a', '#100E08'], Environment: ['#122a18', '#100E08'],
  Economics: ['#132a20', '#100E08'], Science: ['#1e1430', '#100E08'], Media: ['#2a1220', '#100E08'],
  Culture: ['#2a1226', '#100E08'], Sports: ['#2a2410', '#100E08'], Healthcare: ['#2a1418', '#100E08'],
  Law: ['#141a2a', '#100E08'], Society: ['#241a2a', '#100E08'], History: ['#221a12', '#100E08'],
  Philosophy: ['#1a1428', '#100E08'], General: ['#2E2A22', '#100E08'],
};
const coverColors = (cat) => CATEGORY_COVER[cat] || CATEGORY_COVER.General;

// --- SWIPEABLE HOME TOPIC CARD ---
const relTime = (iso) => {
    if (!iso) return '';
    try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return ''; }
};

const HomeTopicCard = ({ topic, onTopicClick, onCommunityClick, isPinned, onPin, onHide, showQuestion = false }) => {
    const [offset, setOffset] = useState(0);
    const [isSwiping, setIsSwiping] = useState(false);

    // Refs for tracking touch geometry
    const startX = useRef(0);
    const startY = useRef(0);

    const ACTION_THRESHOLD = 80; // Distance to trigger action

    useEffect(() => { setOffset(0); }, [topic.id]);

    const handleTouchStart = (e) => {
        startX.current = e.touches[0].clientX;
        startY.current = e.touches[0].clientY;
        setIsSwiping(true);
    };

    const handleTouchMove = (e) => {
        if (!isSwiping) return;
        
        const currentX = e.touches[0].clientX;
        const currentY = e.touches[0].clientY;
        
        const diffX = currentX - startX.current;
        const diffY = currentY - startY.current;

        // 1. HORIZONTAL DOMINANCE CHECK
        // Only trigger swipe if user is moving sideways more than up/down
        if (Math.abs(diffX) > Math.abs(diffY)) {
            
            // 2. SCROLL LOCK
            // Prevent browser navigation/scrolling while swiping
            if (e.cancelable && Math.abs(diffX) > 10) {
                e.preventDefault();
            }

            // 3. SET OFFSET
            if (diffX > 0) {
                // Swipe Right (Positive) -> Hide
                setOffset(Math.min(diffX, 150));
            } else {
                // Swipe Left (Negative) -> Pin
                setOffset(Math.max(diffX, -150)); 
            }
        }
    };

    const handleTouchEnd = () => {
        setIsSwiping(false);
        
        if (offset > ACTION_THRESHOLD) {
            // Right Swipe Action (Hide)
            onHide(topic.id);
            setOffset(500); // Animate off screen
        } else if (offset < -ACTION_THRESHOLD) {
            // Left Swipe Action (Pin)
            onPin(topic.id);
            setOffset(0); // Snap back to show visual state
        } else {
            // Cancel Action
            setOffset(0);
        }
    };

    const handleClick = (e) => {
        if (Math.abs(offset) > 5) return; // Prevent click if dragging
        onTopicClick(e, topic);
    };

    // Opacity Logic
    const hideOpacity = offset > 0 ? Math.min(1, offset / 50) : 0;
    const pinOpacity = offset < 0 ? Math.min(1, Math.abs(offset) / 50) : 0;

    const isSpotlight = topic.isSpotlight === true;
    const category = topic.category || 'General';
    const isCommunityPost = topic.communityId && topic.communityId !== 'null';
    const activeFollowedUsers = topic.activeFollowedUsers || [];

    // Recency signal for the "Active" badge — set server-side on every new post/comment.
    const lastActivityMs = topic.lastActivityAt?.toMillis
        ? topic.lastActivityAt.toMillis()
        : (topic.lastActivityAt?.seconds ? topic.lastActivityAt.seconds * 1000 : 0);
    const isRecentlyActive = lastActivityMs > 0 && (Date.now() - lastActivityMs) < 24 * 60 * 60 * 1000;

    // Opinion split from real debaters (bot/seed posts are excluded server-side).
    const sides = (Array.isArray(topic.sides) && topic.sides.length === 2) ? topic.sides : [{ key: 'A', label: 'For' }, { key: 'B', label: 'Against' }];
    const cA = topic.sideCounts?.A || 0;
    const cB = topic.sideCounts?.B || 0;
    const totalSides = cA + cB;
    const aPct = totalSides ? Math.round((cA / totalSides) * 100) : 50;
    const bPct = 100 - aPct;
    // Only genuinely debatable stories get the "take a side" affordance. Plain news (e.g. an
    // obituary) reads as news — no debate offered. Evergreen topics are always debatable.
    const debatable = topic.isEvergreen || topic.isDebateWorthy || !!topic.debateQuestion || totalSides > 0;

    return (
        <div className="relative overflow-hidden rounded-[3px] group select-none mb-3">
            
            {/* BACKGROUND ACTIONS (swipe) */}
            <div className="absolute inset-0 flex justify-between items-center px-6 bg-edge">
                <div className="flex items-center text-signal font-mono font-semibold uppercase tracking-widest text-xs transition-opacity duration-200" style={{ opacity: hideOpacity }}>
                    <EyeSlashIcon className="w-6 h-6 mr-2" /> Hide
                </div>
                <div className="flex items-center text-bone font-mono font-semibold uppercase tracking-widest text-xs transition-opacity duration-200" style={{ opacity: pinOpacity }}>
                    {isPinned ? 'Unpin' : 'Pin'} <MapPinIcon className="w-6 h-6 ml-2" />
                </div>
            </div>

            {/* FOREGROUND CARD — dark editorial news */}
            <div
                className={`bg-card border border-edge rounded-[3px] flex flex-col cursor-pointer overflow-hidden hover:shadow-[0_18px_40px_-24px_rgba(0,0,0,0.85)] transition-transform duration-200 ease-out relative ${isSpotlight ? '!border-signal border-2' : 'hover:!border-ash/50'} ${isPinned ? 'ring-1 ring-bone/60' : ''}`}
                style={{ transform: `translateX(${offset}px)`, touchAction: 'pan-y' }}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onClick={handleClick}
            >
                {/* IMAGE */}
                <div className="relative w-full h-40 md:h-48 bg-night pointer-events-none">
                    {topic.image ? (
                        <img src={topic.image} alt="" loading="lazy" className="w-full h-full object-cover" />
                    ) : (() => {
                        const catDef = CATEGORIES.find(c => c.id === category);
                        const CoverIcon = catDef?.icon || NewspaperIcon;
                        const [c1, c2] = coverColors(category);
                        return (
                            <div className="w-full h-full relative overflow-hidden flex items-center justify-center px-4" style={{ background: `linear-gradient(135deg, ${c1}, ${c2})` }}>
                                <CoverIcon className="w-28 h-28 text-white/[0.05] absolute -right-3 -bottom-4" />
                                {topic.isEvergreen
                                    ? <h4 className="relative font-serif text-bone/90 text-base md:text-lg font-bold text-center leading-snug line-clamp-3" style={{ textWrap: 'balance' }}>{topic.title}</h4>
                                    : <span className="relative font-mono text-[11px] uppercase tracking-widest text-ash">{category}</span>}
                            </div>
                        );
                    })()}
                    <span className="absolute top-2 left-2 font-mono text-[9px] md:text-[10px] font-semibold text-white bg-black/60 px-2 py-1 uppercase tracking-widest">{category}</span>
                    <div className="absolute top-2 right-2 flex items-center gap-1.5">
                        {topic.isTopicOfDay && (
                            <span className="flex items-center font-mono text-[9px] font-bold text-night bg-amber-300 px-2 py-1 uppercase tracking-wider" title="Today's House Battle"><StarIcon className="w-3 h-3 mr-0.5" /> Topic of Day</span>
                        )}
                        {isRecentlyActive && !topic.isTopicOfDay && (
                            <span className="flex items-center font-mono text-[9px] font-bold text-white bg-signal px-2 py-1 uppercase tracking-wider"><span className="w-1.5 h-1.5 rounded-full bg-white mr-1 animate-pulse" /> Live</span>
                        )}
                    </div>
                    {isPinned && (<div className="absolute bottom-2 right-2 bg-bone text-night p-1"><MapPinIcon className="w-3 h-3" /></div>)}
                </div>

                {/* BODY */}
                <div className="p-3 md:p-4 flex flex-col flex-grow pointer-events-none">
                    {/* newswire meta */}
                    <div className="flex items-center gap-x-2 gap-y-0.5 mb-2 font-mono text-[10px] uppercase tracking-wider flex-wrap">
                        {topic.sourceStation && <span className="text-bone font-semibold">{topic.sourceStation}</span>}
                        {relTime(topic.isoDate) && <span className="text-ash">· {relTime(topic.isoDate)}</span>}
                        {topic.region?.stateCode && topic.region.stateCode !== 'National' && topic.region.stateCode !== 'Timeless' && topic.region.stateCode !== topic.region.countryCode && (
                            <span className="text-ash">· {topic.region.stateCode}</span>
                        )}
                        {topic.sourceLink && (
                            <a href={topic.sourceLink} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="pointer-events-auto text-signal font-semibold hover:underline ml-auto">Read ↗</a>
                        )}
                        {isCommunityPost && (
                            <button onClick={(e) => { e.stopPropagation(); onCommunityClick(e, topic.communityId); }} className="pointer-events-auto flex items-center text-indigo-300 hover:text-indigo-200"><UserGroupIcon className="w-3 h-3 mr-0.5" /> Community</button>
                        )}
                    </div>

                    <h4 className="font-serif text-lg md:text-xl text-bone font-bold leading-[1.12] tracking-tight mb-1.5" style={{ textWrap: 'balance' }}>{showQuestion && topic.debateQuestion ? topic.debateQuestion : topic.title}</h4>
                    <p className="text-xs md:text-sm font-normal line-clamp-2 text-ash mb-3.5">{showQuestion && topic.debateQuestion ? topic.sourceHeadline || topic.summary : topic.summary}</p>

                    {/* OPINION SPLIT — the signature */}
                    <div className="mt-auto pt-3 border-t border-edge">
                        {totalSides > 0 ? (
                            <>
                                <div className="flex h-1.5 overflow-hidden bg-edge rounded-[2px]">
                                    <span className="bg-bone" style={{ width: `${aPct}%` }} />
                                    <span className="bg-signal" style={{ width: `${bPct}%` }} />
                                </div>
                                <div className="flex items-center justify-between mt-2 font-mono text-[10px] uppercase tracking-wide">
                                    <span className="text-bone font-semibold truncate max-w-[38%]">{sides[0].label} {aPct}%</span>
                                    <span className="text-ash tabular-nums">{totalSides} debating</span>
                                    <span className="text-signal font-semibold truncate max-w-[38%] text-right">{bPct}% {sides[1].label}</span>
                                </div>
                            </>
                        ) : debatable ? (
                            <div className="flex items-center justify-between">
                                <span className="font-mono text-[10px] uppercase tracking-wider text-ash">New · be first to weigh in</span>
                                <span className="flex items-center bg-signal text-white font-mono text-[10px] font-semibold px-3 py-1.5 uppercase tracking-wider transition-colors">Take a side <ArrowRightIcon className="w-3.5 h-3.5 ml-1" /></span>
                            </div>
                        ) : (
                            <div className="flex items-center justify-between">
                                <span className="font-mono text-[10px] uppercase tracking-wider text-ash">News</span>
                                <span className="flex items-center text-signal font-mono text-[10px] font-semibold px-3 py-1.5 uppercase tracking-wider">Read the story <ArrowRightIcon className="w-3.5 h-3.5 ml-1" /></span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- ARTICLE READER: read the story in-app, then the debate CTA at the bottom ---
const ArticleReader = ({ topic, onClose, onDebate }) => {
    // Hooks must run before any early return.
    const hasCached = Array.isArray(topic?.articleBlocks) && topic.articleBlocks.length >= 3;
    const [blocks, setBlocks] = useState(() => hasCached ? topic.articleBlocks : null);
    const [byline, setByline] = useState(topic?.articleByline || null);
    const [isBriefing, setIsBriefing] = useState(!!topic?.articleIsBriefing);
    const [status, setStatus] = useState(() => hasCached ? 'ready' : 'loading'); // loading | ready | fallback

    useEffect(() => {
        let alive = true;
        if (!topic) return;
        if (status === 'ready') return; // already have cached blocks
        // No source link → the briefing layer can still run server-side off the topic id.
        setStatus('loading');
        getArticleContent({ url: topic.sourceLink || '', topicId: topic.id })
            .then((res) => {
                if (!alive) return;
                if (res && res.ok && Array.isArray(res.blocks) && res.blocks.length >= 3) {
                    setBlocks(res.blocks);
                    if (res.byline) setByline(res.byline);
                    setIsBriefing(!!res.isBriefing);
                    setStatus('ready');
                } else {
                    setStatus('fallback');
                }
            })
            .catch(() => { if (alive) setStatus('fallback'); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [topic?.id]);

    if (!topic) return null;
    const category = topic.category || 'General';
    const sides = (Array.isArray(topic.sides) && topic.sides.length === 2) ? topic.sides : [{ key: 'A', label: 'For' }, { key: 'B', label: 'Against' }];
    const total = (topic.sideCounts?.A || 0) + (topic.sideCounts?.B || 0);
    const showRegion = topic.region?.stateCode && topic.region.stateCode !== 'National' && topic.region.stateCode !== 'Timeless' && topic.region.stateCode !== topic.region.countryCode;
    // Only offer the debate on genuinely debatable stories; plain news is read-only.
    const debatable = topic.isEvergreen || topic.isDebateWorthy || !!topic.debateQuestion || total > 0;
    const debatePrompt = topic.debateQuestion || topic.title;

    // First paragraph gets the drop cap; the rest read as clean body.
    let firstParaSeen = false;
    const renderBlock = (b, i) => {
        if (b.type === 'h') {
            return <h2 key={i} className="font-serif text-xl md:text-2xl font-bold text-bone leading-snug mt-8 mb-3" style={{ textWrap: 'balance' }}>{b.text}</h2>;
        }
        const isFirst = !firstParaSeen;
        if (isFirst) firstParaSeen = true;
        return (
            <p key={i} className={`text-[1.05rem] md:text-lg leading-[1.75] text-bone/85 mb-5 ${isFirst ? 'first-letter:font-serif first-letter:text-signal first-letter:font-bold first-letter:text-6xl first-letter:float-left first-letter:mr-3 first-letter:mt-1 first-letter:leading-[0.72]' : ''}`}>{b.text}</p>
        );
    };

    return (
        <div className="fixed inset-0 z-[70] bg-night text-bone overflow-y-auto animate-fade-in" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
            {/* top bar */}
            <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 bg-night/90 backdrop-blur border-b border-edge">
                <button onClick={onClose} className="flex items-center font-mono text-[11px] uppercase tracking-wider text-ash hover:text-bone transition-colors"><ArrowLeftIcon className="w-4 h-4 mr-1.5" /> Back</button>
                <span className="font-serif font-bold text-bone text-lg">Verbi<span className="text-signal">.</span></span>
                {topic.sourceLink ? <a href={topic.sourceLink} target="_blank" rel="noopener noreferrer" className="font-mono text-[11px] uppercase tracking-wider text-signal font-semibold">Source&nbsp;↗</a> : <span className="w-12" />}
            </div>

            <article className="max-w-2xl mx-auto px-5 pb-14">
                {/* hero */}
                <div className="relative -mx-5 mb-5 h-52 md:h-72 bg-night">
                    {topic.image
                        ? <img src={topic.image} alt="" className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-edge to-night"><NewspaperIcon className="w-12 h-12 text-ash/50" /></div>}
                    <span className="absolute top-3 left-3 font-mono text-[10px] font-semibold text-white bg-black/60 px-2 py-1 uppercase tracking-widest">{category}</span>
                </div>

                {/* byline */}
                <div className="font-mono text-[11px] uppercase tracking-wider text-ash flex flex-wrap gap-x-2 gap-y-1 border-b border-edge pb-3 mb-5">
                    {topic.sourceStation && <span className="text-bone font-semibold">{topic.sourceStation}</span>}
                    {byline && <span>· {byline}</span>}
                    {relTime(topic.isoDate) && <span>· {relTime(topic.isoDate)}</span>}
                    {showRegion && <span>· {topic.region.stateCode}</span>}
                </div>

                {/* headline */}
                <h1 className="font-serif text-2xl md:text-4xl font-bold text-bone leading-[1.08] tracking-tight mb-6" style={{ textWrap: 'balance' }}>{topic.title}</h1>

                {/* body */}
                {status === 'loading' && (
                    <div className="mb-8">
                        {topic.summary && <p className="text-[1.05rem] md:text-lg leading-[1.75] text-bone/85 mb-6">{topic.summary}</p>}
                        <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-ash mb-5">
                            <span className="w-2 h-2 rounded-full bg-signal animate-pulse" /> Loading the full story…
                        </div>
                        <div className="space-y-3 animate-pulse" aria-hidden="true">
                            {[...Array(6)].map((_, i) => <div key={i} className="h-3.5 rounded bg-edge" style={{ width: `${92 - (i % 3) * 12}%` }} />)}
                        </div>
                    </div>
                )}

                {status === 'ready' && blocks && (
                    <div className="mb-8">
                        {isBriefing && (
                            <div className="flex items-center gap-2 mb-5 rounded-[3px] border border-edge bg-card px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-ash">
                                <span className="w-1.5 h-1.5 rounded-full bg-signal" /> Verbi Briefing · original context, not the publisher’s text
                            </div>
                        )}
                        {blocks.map(renderBlock)}
                    </div>
                )}

                {status === 'fallback' && (
                    <div className="mb-8">
                        <p className="text-[1.05rem] md:text-lg leading-[1.75] text-bone/85 mb-6 first-letter:font-serif first-letter:text-signal first-letter:font-bold first-letter:text-6xl first-letter:float-left first-letter:mr-3 first-letter:mt-1 first-letter:leading-[0.72]">{topic.summary || 'The full story is available at the source below.'}</p>
                        <div className="rounded-[3px] border border-edge bg-card px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-ash">This publisher doesn’t allow in-app reading. Open the full story at the source.</div>
                    </div>
                )}

                {topic.sourceLink && (
                    <a href={topic.sourceLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center font-mono text-[12px] uppercase tracking-wider text-signal font-semibold mb-9 hover:underline">
                        {(status === 'ready' && !isBriefing) ? 'View original at' : 'Read the original coverage at'} {topic.sourceStation || 'the source'}&nbsp;↗
                    </a>
                )}

                {/* black/red debate CTA — only for debatable stories; plain news is read-only */}
                {debatable && (
                <div className="relative overflow-hidden bg-black text-white rounded-[3px] p-6 md:p-7 border border-edge">
                    <div className="absolute top-0 left-0 right-0 h-[3px] bg-signal" />
                    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/55 mb-2">Now — where do you stand?</div>
                    <h3 className="font-serif text-xl md:text-2xl font-bold leading-snug mb-5" style={{ textWrap: 'balance' }}>{debatePrompt}</h3>
                    <div className="flex gap-2.5 mb-3">
                        <button onClick={() => onDebate(topic, sides[0].key)} className="flex-1 font-mono text-[12px] font-semibold uppercase tracking-wider py-3 rounded-[3px] border border-white/25 hover:bg-white/10 transition-colors">{sides[0].label}</button>
                        <button onClick={() => onDebate(topic, sides[1].key)} className="flex-1 font-mono text-[12px] font-semibold uppercase tracking-wider py-3 rounded-[3px] border border-signal hover:bg-signal/20 transition-colors">{sides[1].label}</button>
                    </div>
                    <button onClick={() => onDebate(topic)} className="w-full font-bold text-sm py-3.5 rounded-[3px] bg-signal text-white flex items-center justify-center gap-2 hover:brightness-110 transition-all">Join the debate <ArrowRightIcon className="w-4 h-4" /></button>
                    <div className="text-center font-mono text-[10px] text-white/50 mt-3 tracking-wide">{total > 0 ? `${total} debating · your argument gets scored` : 'Be the first · your argument gets scored'}</div>
                </div>
                )}
            </article>
        </div>
    );
};

// --- NATIVE FEED AD SLOT (non-annoying: labeled, low-frequency, dismissible, Pro-exempt) ---
// Designed to sit inline between news cards without breaking the read. Kept low-frequency
// (AD_FREQUENCY) and always clearly labeled. Right now it rotates in-house promos; when a real
// ad network is wired, render its unit here and label it "Sponsored / Ad" (required for honesty).
const ADS_ENABLED = true;
const AD_FREQUENCY = 8; // one slot per N cards — deliberately sparse so it never feels spammy

// --- REAL ADS (Google AdSense) ---
// Set these two to your AdSense IDs to switch the boxes from house promos to REAL paid ads.
// AD_CLIENT: your publisher id, e.g. 'ca-pub-1234567890123456'
// AD_SLOT:   an in-feed/display ad unit slot id from your AdSense dashboard, e.g. '1234567890'
// When both are set the slot renders a live AdSense unit; otherwise it shows an in-house promo.
// Pro users never see either (handled where the slot is interleaved).
const AD_CLIENT = '';
const AD_SLOT = '';

let _adsenseScriptLoaded = false;
function loadAdSense(client) {
  if (_adsenseScriptLoaded || !client || typeof document === 'undefined') return;
  _adsenseScriptLoaded = true;
  const s = document.createElement('script');
  s.async = true;
  s.crossOrigin = 'anonymous';
  s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${client}`;
  document.head.appendChild(s);
}

const AdSenseUnit = () => {
  useEffect(() => {
    loadAdSense(AD_CLIENT);
    try { (window.adsbygoogle = window.adsbygoogle || []).push({}); } catch (e) { /* not loaded yet */ }
  }, []);
  return (
    <ins className="adsbygoogle" style={{ display: 'block' }} data-ad-client={AD_CLIENT} data-ad-slot={AD_SLOT} data-ad-format="fluid" data-full-width-responsive="true" />
  );
};

const HOUSE_ADS = [
  { tag: 'Verbi Pro', title: 'Go ad-free and unlock AI coaching', cta: 'See Pro', to: '/store' },
  { tag: 'Invite', title: 'A debate is better with a worthy opponent', cta: 'Invite a friend', to: '/profile' },
  { tag: 'Arena', title: 'Think you can take on ten at once?', cta: 'Enter the 10v1', to: '/arena10v1-lobby' },
];

const FeedAdSlot = ({ index, onDismiss, navigate }) => {
  // Real AdSense ad when configured; otherwise a tasteful in-house promo.
  if (AD_CLIENT && AD_SLOT) {
    return (
      <div className="relative bg-card border border-edge rounded-[3px] p-3 overflow-hidden min-h-[90px]">
        <div className="font-mono text-[9px] uppercase tracking-widest text-ash mb-1.5">Sponsored</div>
        <AdSenseUnit key={`ad_${index}`} />
      </div>
    );
  }
  const ad = HOUSE_ADS[index % HOUSE_ADS.length];
  return (
    <div className="relative bg-card border border-edge rounded-[3px] p-4 overflow-hidden">
      <button onClick={onDismiss} aria-label="Dismiss" className="absolute top-2 right-2 p-1 text-ash hover:text-bone transition-colors z-10"><XMarkIcon className="w-3.5 h-3.5" /></button>
      <div className="font-mono text-[9px] uppercase tracking-widest text-ash mb-2">Promoted · {ad.tag}</div>
      <h4 className="font-serif text-base md:text-lg font-bold text-bone leading-snug mb-3 pr-6" style={{ textWrap: 'balance' }}>{ad.title}</h4>
      <button onClick={() => navigate(ad.to)} className="inline-flex items-center font-mono text-[11px] uppercase tracking-wider text-signal font-semibold hover:underline">{ad.cta} <ArrowRightIcon className="w-3 h-3 ml-1" /></button>
    </div>
  );
};

// --- PAYWALL MODAL ---
const PaywallModal = ({ onClose }) => {
    const navigate = useNavigate();

    const handleGoToMarketplace = () => {
        onClose();
        navigate('/store');
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-md animate-fade-in">
            <div className="bg-white w-full max-w-md rounded-3xl overflow-hidden shadow-2xl relative border-4 border-slate-900">
                <button onClick={onClose} className="absolute top-4 right-4 p-2 bg-slate-100 rounded-full text-slate-500 hover:bg-slate-200 transition-colors z-10">
                    <XMarkIcon className="w-5 h-5" />
                </button>
                
                <div className="bg-slate-50 p-6 md:p-8 text-center relative border-b border-slate-100">
                    <StarIcon className="w-12 md:w-16 h-12 md:h-16 text-yellow-500 mx-auto mb-4 animate-pulse drop-shadow-md" />
                    <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tight mb-2 text-slate-900">Upgrade to Pro</h2>
                    <p className="text-sm md:text-base text-slate-500 font-medium">Unlock the full potential of Verbi.</p>
                </div>

                <div className="p-6 md:p-8 space-y-4">
                    <div className="space-y-3 mb-6">
                        <div className="flex items-center text-slate-700 font-bold text-xs md:text-sm">
                            <CheckIcon className="w-5 h-5 text-green-600 mr-3" />
                            10 Private Lobbies / Month
                        </div>
                        <div className="flex items-center text-slate-700 font-bold text-xs md:text-sm">
                            <CheckIcon className="w-5 h-5 text-green-600 mr-3" />
                            Access "The Sensei" (AI Coaching)
                        </div>
                        <div className="flex items-center text-slate-700 font-bold text-xs md:text-sm">
                            <CheckIcon className="w-5 h-5 text-green-600 mr-3" />
                            Grandmaster AI Difficulty
                        </div>
                        <div className="flex items-center text-slate-700 font-bold text-xs md:text-sm">
                            <CheckIcon className="w-5 h-5 text-green-600 mr-3" />
                            Create Custom Topics
                        </div>
                        <div className="flex items-center text-slate-700 font-bold text-xs md:text-sm">
                            <CheckIcon className="w-5 h-5 text-green-600 mr-3" />
                            Exclusive Pro Badge
                        </div>
                        <div className="flex items-center text-slate-700 font-bold text-xs md:text-sm">
                            <GiftIcon className="w-5 h-5 text-purple-600 mr-3" />
                            Unlock Title: Sir / Dame
                        </div>
                    </div>

                    <button
                        onClick={handleGoToMarketplace}
                        className="w-full bg-slate-900 text-white font-black py-3 md:py-4 rounded-xl uppercase tracking-widest hover:bg-slate-800 transition-all shadow-lg active:scale-95 flex justify-center items-center text-sm md:text-base"
                    >
                        Go to Marketplace
                    </button>

                    <p className="text-center text-[8px] md:text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-4">
                        Secure Payment via App Store
                    </p>
                </div>
            </div>
        </div>
    );
};

// --- ACTIVE SESSIONS LIST MODAL ---
const ActiveSessionsModal = ({ onClose, matches, currentUser, onRefresh }) => {
    const navigate = useNavigate();
    const [deletingId, setDeletingId] = useState(null);
    const { confirm, ConfirmDialog } = useConfirm();
    const { showToast } = useToast();

    // Pagination
    const ITEMS_PER_PAGE = 5;
    const [currentPage, setCurrentPage] = useState(0);
    const totalPages = Math.ceil(matches.length / ITEMS_PER_PAGE);
    const paginatedMatches = matches.slice(currentPage * ITEMS_PER_PAGE, (currentPage + 1) * ITEMS_PER_PAGE);

    const handleDelete = async (e, matchId) => {
        e.stopPropagation();

        const confirmed = await confirm({
            title: 'Delete Session?',
            message: 'Are you sure you want to delete this session? This action cannot be undone.',
            confirmText: 'Delete',
            cancelText: 'Cancel',
            variant: 'danger'
        });

        if (!confirmed) return;

        setDeletingId(matchId);
        try {
            await deleteDebate(matchId);
            if (onRefresh) onRefresh(); // Refresh list
        } catch (error) {
            showToast("Failed to delete session", "error");
        }
        setDeletingId(null);
    };

    const handleNavigate = (match) => {
        // 10v1 games navigate to arena10v1 route
        if (match.type === '10v1') {
            navigate(`/arena10v1/${match.id}`);
        } else {
            navigate(`/arena/${match.id}`);
        }
        onClose();
    };

    return (
        <>
        <ConfirmDialog />
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm animate-fade-in">
            <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl flex flex-col animate-slide-up overflow-hidden max-h-[80vh]">
                <div className="p-4 md:p-5 bg-slate-900 text-white flex justify-between items-center flex-shrink-0">
                    <div className="flex items-center">
                        <BoltIcon className="w-5 md:w-6 h-5 md:h-6 mr-2 text-green-500 animate-pulse" />
                        <div>
                            <h2 className="text-base md:text-lg font-black uppercase tracking-wider">Active Sessions</h2>
                            <p className="text-[10px] md:text-xs text-slate-400 font-bold">Your Active Debates</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1 hover:bg-slate-800 rounded-full transition-colors">
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>

                <div className="p-4 overflow-y-auto bg-slate-50 flex-1 space-y-3">
                    {matches.length === 0 ? (
                        <div className="text-center py-10 text-slate-400 font-medium">
                            No active sessions found.
                        </div>
                    ) : (
                        paginatedMatches.map(match => {
                            const is10v1 = match.type === '10v1';
                            const isAiMatch = match.type === 'ai_match';
                            const isPrivate = match.visibility === 'private';
                            const isTurnActive = match.status === 'active';
                            const isMod = match.moderatorId === currentUser.uid;
                            const isOwner = match.ownerId === currentUser.uid;
                            const isDefender = is10v1 && match.defender === currentUser.uid;
                            const isChallenger = is10v1 && match.challengers?.includes(currentUser.uid);
                            // For AI matches, the human player's side is stored in aiMetadata
                            const isAiPlayer = isAiMatch && match.aiMetadata?.humanSide;
                            // Check if user is in either team
                            const isTeamMember = match.teams?.A?.members?.includes(currentUser.uid) || match.teams?.B?.members?.includes(currentUser.uid);
                            // User can delete if they're involved in the match
                            const canDelete = isOwner || isMod || isDefender || isAiPlayer || (isTeamMember && match.status !== 'active');

                            let statusLabel = "LOBBY";
                            let statusColor = "bg-blue-100 text-blue-700";

                            if (isTurnActive) {
                                statusLabel = "LIVE";
                                statusColor = "bg-green-100 text-green-700 animate-pulse";
                            } else if (match.status === 'completed') {
                                statusLabel = "ENDED";
                                statusColor = "bg-slate-200 text-slate-600";
                            }

                            // 10v1 specific icon and colors
                            const getIcon = () => {
                                if (is10v1) {
                                    return <FireIcon className="w-5 md:w-6 h-5 md:h-6" />;
                                }
                                return isPrivate ? <LockClosedIcon className="w-5 md:w-6 h-5 md:h-6" /> : <UserGroupIcon className="w-5 md:w-6 h-5 md:h-6" />;
                            };

                            const getIconBgColor = () => {
                                if (is10v1) {
                                    return isDefender ? 'bg-blue-500 text-white' : 'bg-red-500 text-white';
                                }
                                return isPrivate ? 'bg-slate-900 text-white' : 'bg-yellow-400 text-slate-900';
                            };

                            return (
                                <div
                                    key={match.id}
                                    onClick={() => handleNavigate(match)}
                                    className="bg-white p-3 md:p-4 rounded-xl border-2 border-slate-200 shadow-sm hover:border-slate-900 hover:shadow-md transition-all cursor-pointer flex justify-between items-center group relative"
                                >
                                    <div className="flex items-center overflow-hidden flex-grow pr-2">
                                        <div className={`w-10 md:w-12 h-10 md:h-12 rounded-xl flex items-center justify-center mr-3 md:mr-4 flex-shrink-0 ${getIconBgColor()}`}>
                                            {getIcon()}
                                        </div>
                                        <div className="min-w-0">
                                            <h4 className="font-black text-slate-900 text-xs md:text-sm break-words whitespace-normal leading-tight mb-1.5">
                                                {match.title || "Untitled Match"}
                                            </h4>
                                            <div className="flex items-center gap-2 flex-wrap">
                                                {is10v1 && (
                                                    <span className="text-[8px] md:text-[9px] font-black uppercase px-2 py-0.5 rounded bg-orange-100 text-orange-700">
                                                        10v1
                                                    </span>
                                                )}
                                                <span className={`text-[8px] md:text-[9px] font-black uppercase px-2 py-0.5 rounded ${statusColor}`}>
                                                    {statusLabel}
                                                </span>
                                                {is10v1 && isDefender && (
                                                    <span className="text-[8px] md:text-[9px] font-black uppercase px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                                                        Defender
                                                    </span>
                                                )}
                                                {is10v1 && isChallenger && (
                                                    <span className="text-[8px] md:text-[9px] font-black uppercase px-2 py-0.5 rounded bg-red-100 text-red-700">
                                                        Challenger
                                                    </span>
                                                )}
                                                {isMod && <span className="text-[8px] md:text-[9px] font-black uppercase px-2 py-0.5 rounded bg-indigo-100 text-indigo-700">Moderator</span>}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center space-x-2 flex-shrink-0">
                                        {/* DELETE BUTTON: Show for users who can delete this session */}
                                        {canDelete && (
                                            <button
                                                onClick={(e) => handleDelete(e, match.id)}
                                                disabled={deletingId === match.id}
                                                className="p-2 bg-red-50 text-red-500 rounded-lg hover:bg-red-100 transition-colors border border-red-100"
                                                title="End/Delete Session"
                                            >
                                                {deletingId === match.id ? <ArrowPathIcon className="w-5 h-5 animate-spin"/> : <TrashIcon className="w-5 h-5" />}
                                            </button>
                                        )}
                                        <PlayCircleIcon className="w-8 h-8 text-slate-300 group-hover:text-slate-900 transition-colors" />
                                    </div>
                                </div>
                            );
                        })
                    )}

                    {/* Pagination Controls */}
                    {matches.length > ITEMS_PER_PAGE && (
                        <div className="flex items-center justify-between pt-3 border-t border-slate-200 mt-3">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                                disabled={currentPage === 0}
                                className="flex items-center space-x-1 px-3 py-1.5 rounded-lg text-xs font-bold text-slate-600 hover:text-slate-900 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                            >
                                <ChevronLeftIcon className="w-4 h-4" />
                                <span>Prev</span>
                            </button>
                            <span className="text-xs text-slate-500 font-medium">
                                Page {currentPage + 1} of {totalPages}
                            </span>
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                                disabled={currentPage >= totalPages - 1}
                                className="flex items-center space-x-1 px-3 py-1.5 rounded-lg text-xs font-bold text-slate-600 hover:text-slate-900 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                            >
                                <span>Next</span>
                                <ChevronRightIcon className="w-4 h-4" />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
        </>
    );
};

// --- Lobby Browser Modal ---
const LobbyBrowser = ({ onClose }) => {
  const navigate = useNavigate();
  const [lobbies, setLobbies] = useState([]);
  const [loading, setLoading] = useState(true);
  const { showToast } = useToast();

  const [joinCode, setJoinCode] = useState('');
  const [isJoiningPrivate, setIsJoiningPrivate] = useState(false);

  useEffect(() => {
    getOpenLobbies().then(data => {
      setLobbies(data);
      setLoading(false);
    });
  }, []);

  const handleCreateClick = () => {
    navigate('/arena/setup');
  };

  const handleJoinPrivate = async (e) => {
      e.preventDefault();
      if (!joinCode.trim()) return;
      setIsJoiningPrivate(true);
      try {
          const gameId = await joinPrivateGame(joinCode);
          navigate(`/arena/${gameId}`);
      } catch (error) {
          showToast(error.message || "Failed to join game", "error");
          setIsJoiningPrivate(false);
      }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl max-h-[80vh] flex flex-col animate-slide-up overflow-hidden">
        <div className="p-4 bg-slate-900 text-white flex justify-between items-center">
          <h2 className="text-base md:text-lg font-black uppercase tracking-wider flex items-center">
            <GlobeAltIcon className="w-5 md:w-6 h-5 md:h-6 mr-2 text-yellow-500" />
            Public Lobbies
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-800 rounded-full transition-colors">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-4 bg-slate-50 border-b border-slate-200">
            <form onSubmit={handleJoinPrivate} className="flex gap-2">
                <div className="relative flex-grow">
                    <KeyIcon className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input 
                        type="text" 
                        value={joinCode}
                        onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                        placeholder="Enter Private Code" 
                        className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-slate-200 font-bold text-slate-900 placeholder:text-slate-400 focus:border-slate-900 outline-none uppercase"
                    />
                </div>
                <button 
                    type="submit"
                    disabled={isJoiningPrivate || !joinCode}
                    className="bg-slate-900 text-white font-bold px-6 rounded-xl hover:bg-slate-800 disabled:opacity-50 transition-colors"
                >
                    {isJoiningPrivate ? <ArrowPathIcon className="w-5 h-5 animate-spin" /> : 'JOIN'}
                </button>
            </form>
        </div>

        <div className="flex-1 overflow-y-auto p-4 bg-white">
          <h3 className="text-[10px] md:text-xs font-black text-slate-400 uppercase tracking-widest mb-3">Open Sessions</h3>
          {loading ? (
            <div className="text-center py-10 text-slate-500 flex flex-col items-center">
              <ArrowPathIcon className="w-8 h-8 animate-spin mb-2" />
              Scanning frequency...
            </div>
          ) : lobbies.length === 0 ? (
            <div className="text-center py-10 text-slate-500">
              <p className="mb-4 font-medium">No open public lobbies found.</p>
              <p className="text-sm">Start a new one below!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {lobbies.map(lobby => {
                  const maxPlayers = lobby.teamSize * 2;
                  const currentPlayers = lobby.playerCount || 0;
                  const isFull = currentPlayers >= maxPlayers;

                  return (
                    <div key={lobby.id} className="bg-white p-3 md:p-4 rounded-xl border-2 border-slate-100 hover:border-slate-300 flex flex-col shadow-sm transition-all">
                      <div className="flex justify-between items-start mb-3">
                          <div>
                            <h3 className="font-bold text-slate-900 line-clamp-1 text-xs md:text-sm">{lobby.title}</h3>
                            <div className="flex items-center text-[8px] md:text-[10px] text-slate-500 mt-1 font-bold uppercase tracking-wider">
                              <ClockIcon className="w-3 h-3 mr-1" />
                              {formatDistanceToNow(lobby.createdAt?.toDate(), { addSuffix: true })}
                              <span className="mx-2">•</span>
                              {lobby.mode === 'blitz' ? 'Blitz' : 'Classic'}
                            </div>
                          </div>
                          <div className="text-right">
                              <div className={`text-[10px] md:text-xs font-black ${isFull ? 'text-red-500' : 'text-green-500'}`}>
                                  {currentPlayers} / {maxPlayers}
                              </div>
                              <div className="text-[8px] md:text-[9px] font-bold text-slate-400 uppercase">PLAYERS</div>
                          </div>
                      </div>

                      {/* Capacity Bar */}
                      <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden mb-4">
                          <div
                            className={`h-full ${isFull ? 'bg-red-500' : 'bg-green-500'}`}
                            style={{ width: `${(currentPlayers / maxPlayers) * 100}%` }}
                          ></div>
                      </div>

                      <button
                        onClick={() => navigate(`/arena/${lobby.id}`)}
                        disabled={isFull}
                        className={`w-full font-bold py-2 md:py-2.5 rounded-lg text-[10px] md:text-xs uppercase tracking-wider transition-colors flex items-center justify-center ${isFull ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-slate-900 text-white hover:bg-slate-800'}`}
                      >
                        {isFull ? 'Lobby Full' : 'Join Game'}
                      </button>
                    </div>
                  );
              })}
            </div>
          )}
        </div>

        <div className="p-4 bg-white border-t border-slate-100">
          <button
            onClick={handleCreateClick}
            className="w-full bg-yellow-400 text-slate-900 font-black py-2.5 md:py-3 rounded-xl uppercase tracking-wider flex items-center justify-center hover:bg-yellow-300 transition-colors shadow-sm text-xs md:text-sm"
          >
            <PlusIcon className="w-4 md:w-5 h-4 md:h-5 mr-2" />
            HOST NEW MATCH
          </button>
        </div>
      </div>
    </div>
  );
};

// --- AI SETUP MODAL ---
const AiSetupModal = ({ onClose }) => {
  const navigate = useNavigate();
  const { isPro } = useAuth();
  const { showToast } = useToast();

  // CONFIG STATE
  const [selectedCategory, setSelectedCategory] = useState('General');
  const [difficulty, setDifficulty] = useState('gauntlet');
  const [rounds, setRounds] = useState(3);

  // PREVIEW STATE
  const [step, setStep] = useState('config'); // 'config' | 'preview'
  const [generatedTopic, setGeneratedTopic] = useState(null);
  const [userSide, setUserSide] = useState(null);
  const [loading, setLoading] = useState(false);

  const [showPaywall, setShowPaywall] = useState(false);

  const handleDifficultySelect = (mode) => {
      if (mode === 'sensei' && !isPro) {
          setShowPaywall(true);
          return;
      }
      setDifficulty(mode);
  };

  // STEP 1: GENERATE
  const handleGenerate = async () => {
      setLoading(true);
      try {
          const topicData = await generateTrainingTopic(selectedCategory, difficulty);
          setGeneratedTopic(topicData);
          setStep('preview');
      } catch (e) {
          showToast("Failed to generate scenario", "error");
      }
      setLoading(false);
  };

  // STEP 2: START
  const handleStart = async () => {
    if (!userSide) {
        showToast("Please select a side", "warning");
        return;
    }
    setLoading(true);
    try {
      const topicId = await createTemporaryTopic(generatedTopic);
      const debateId = await createAiDebate(topicId, difficulty, userSide, rounds);
      navigate(`/debate/${debateId}`);
    } catch (e) {
      console.error(e);
      showToast("Failed to initialize match", "error");
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm animate-fade-in">
      
      {showPaywall && <PaywallModal onClose={() => setShowPaywall(false)} />}

      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl flex flex-col animate-slide-up overflow-hidden max-h-[90vh]">
        
        {/* HEADER */}
        <div className="p-4 md:p-6 bg-slate-900 text-white flex-shrink-0">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-lg md:text-xl font-black uppercase tracking-wider flex items-center">
              <CpuChipIcon className="w-5 md:w-6 h-5 md:h-6 mr-2 text-blue-500" />
              {step === 'config' ? 'Sim Configuration' : 'Mission Briefing'}
            </h2>
            <button onClick={onClose}><XMarkIcon className="w-6 h-6 text-slate-400 hover:text-white" /></button>
          </div>
          <p className="text-slate-400 text-xs md:text-sm font-medium">
              {step === 'config' ? 'Configure your training parameters.' : 'Review the scenario and choose your stance.'}
          </p>
        </div>

        {/* STEP 1: CONFIGURATION */}
        {step === 'config' && (
            <div className="p-4 md:p-6 overflow-y-auto space-y-5 md:space-y-6 bg-slate-50 flex-1">
                {/* Difficulty */}
                <div>
                    <label className="block text-[10px] md:text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 md:mb-3">Select Mode</label>
                    <div className="grid grid-cols-2 gap-2 md:gap-3">
                        <button
                            onClick={() => handleDifficultySelect('gauntlet')}
                            className={`relative p-3 md:p-4 rounded-xl text-left border-2 transition-all duration-200 ${difficulty === 'gauntlet' ? 'bg-white border-slate-900 shadow-md' : 'bg-slate-100 border-transparent hover:bg-white hover:border-slate-200'}`}
                        >
                            <div className="flex items-center mb-1">
                                <BoltIcon className={`w-4 md:w-5 h-4 md:h-5 mr-2 ${difficulty === 'gauntlet' ? 'text-orange-500' : 'text-slate-400'}`} />
                                <span className={`font-black text-xs md:text-sm uppercase ${difficulty === 'gauntlet' ? 'text-slate-900' : 'text-slate-500'}`}>The Gauntlet</span>
                            </div>
                            <p className="text-[8px] md:text-[10px] text-slate-500 font-medium leading-tight">
                                Standard Challenge. Face a Grandmaster-level adversary.
                            </p>
                        </button>

                        <button
                            onClick={() => handleDifficultySelect('sensei')}
                            className={`relative p-3 md:p-4 rounded-xl text-left border-2 transition-all duration-200 overflow-hidden ${difficulty === 'sensei' ? 'bg-white border-slate-900 shadow-md' : 'bg-slate-100 border-transparent hover:bg-white hover:border-slate-200'}`}
                        >
                            <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center">
                                    <AcademicCapIcon className={`w-4 md:w-5 h-4 md:h-5 mr-2 ${difficulty === 'sensei' ? 'text-purple-500' : 'text-slate-400'}`} />
                                    <span className={`font-black text-xs md:text-sm uppercase ${difficulty === 'sensei' ? 'text-slate-900' : 'text-slate-500'}`}>The Sensei</span>
                                </div>
                                {!isPro && <span className="bg-yellow-400 text-slate-900 text-[8px] md:text-[10px] font-black px-1.5 py-0.5 rounded shadow-sm">PRO</span>}
                            </div>
                            <p className="text-[8px] md:text-[10px] text-slate-500 font-medium leading-tight">
                                Challenge + Coaching. Real-time critiques & suggestions.
                            </p>
                        </button>
                    </div>
                </div>

                {/* Rounds */}
                <div>
                    <label className="block text-[10px] md:text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 md:mb-3">Match Length (Rounds)</label>
                    <div className="grid grid-cols-4 gap-2 md:gap-3">
                        {[3, 5, 7, 10].map(r => (
                            <button
                                key={r}
                                onClick={() => setRounds(r)}
                                className={`py-2.5 md:py-3 rounded-xl font-bold uppercase text-xs md:text-sm transition-all ${rounds === r ? 'bg-slate-900 text-white shadow-md' : 'bg-white text-slate-500 border-2 border-slate-200 hover:border-slate-400'}`}
                            >
                                {r}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Category */}
                <div>
                    <label className="block text-[10px] md:text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 md:mb-3">Target Sector</label>
                    <div className="grid grid-cols-2 gap-2">
                        {CATEGORIES.filter(c => c.id !== null).map(cat => (
                            <button
                                key={cat.id}
                                onClick={() => setSelectedCategory(cat.id)}
                                className={`flex items-center p-2 rounded-lg border-2 transition-all ${selectedCategory === cat.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}
                            >
                                <cat.icon className={`w-3.5 md:w-4 h-3.5 md:h-4 mr-2 ${selectedCategory === cat.id ? 'text-yellow-400' : 'text-slate-400'}`} />
                                <span className="text-[10px] md:text-xs font-bold uppercase tracking-wide">{cat.label}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        )}

        {/* STEP 2: PREVIEW & SIDE SELECT */}
        {step === 'preview' && generatedTopic && (
            <div className="p-4 md:p-6 overflow-y-auto space-y-5 md:space-y-6 bg-slate-50 flex-1">
                <div className="bg-white p-4 md:p-5 rounded-2xl border border-slate-200 shadow-sm text-center">
                    <span className="inline-block px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-[8px] md:text-[10px] font-black uppercase tracking-widest mb-3 border border-blue-100">
                        {selectedCategory} Scenario
                    </span>
                    <h3 className="text-base md:text-lg font-black text-slate-900 leading-tight mb-2">
                        "{generatedTopic.title}"
                    </h3>
                    <p className="text-xs md:text-sm text-slate-500 font-medium leading-relaxed">
                        {generatedTopic.summary}
                    </p>
                </div>

                <div>
                    <label className="block text-[10px] md:text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 md:mb-3 text-center">Choose Your Stance</label>
                    <div className="flex gap-3 md:gap-4">
                        <button
                            onClick={() => setUserSide('A')}
                            className={`flex-1 py-3 md:py-4 px-3 md:px-4 rounded-xl font-bold text-xs md:text-sm uppercase tracking-wider transition-all border-2 flex flex-col items-center justify-center ${userSide === 'A' ? 'bg-slate-900 text-white border-slate-900 shadow-lg scale-105' : 'bg-white text-slate-900 border-slate-200 hover:border-slate-400'}`}
                        >
                            <span className="mb-1 text-base md:text-lg">A</span>
                            {generatedTopic.sides?.[0]?.label || 'Affirmative'}
                        </button>
                        <button
                            onClick={() => setUserSide('B')}
                            className={`flex-1 py-3 md:py-4 px-3 md:px-4 rounded-xl font-bold text-xs md:text-sm uppercase tracking-wider transition-all border-2 flex flex-col items-center justify-center ${userSide === 'B' ? 'bg-slate-900 text-white border-slate-900 shadow-lg scale-105' : 'bg-white text-slate-900 border-slate-200 hover:border-slate-400'}`}
                        >
                            <span className="mb-1 text-base md:text-lg">B</span>
                            {generatedTopic.sides?.[1]?.label || 'Negative'}
                        </button>
                    </div>
                </div>
            </div>
        )}

        {/* FOOTER ACTIONS */}
        <div className="p-4 bg-white border-t border-slate-100 flex-shrink-0">
          {step === 'config' ? (
              <button
                onClick={handleGenerate}
                disabled={loading}
                className="w-full bg-blue-600 text-white font-black py-3 md:py-4 rounded-xl uppercase tracking-widest hover:bg-blue-500 disabled:opacity-50 transition-colors flex items-center justify-center shadow-lg shadow-blue-200 text-xs md:text-sm"
              >
                {loading ? (
                    <>
                        <ArrowPathIcon className="w-4 md:w-5 h-4 md:h-5 animate-spin mr-2" />
                        GENERATING...
                    </>
                ) : (
                    <>
                        <BoltIcon className="w-4 md:w-5 h-4 md:h-5 mr-2" />
                        GENERATE SCENARIO
                    </>
                )}
              </button>
          ) : (
              <div className="flex gap-2 md:gap-3">
                  <button
                      onClick={() => { setStep('config'); setUserSide(null); }}
                      className="px-4 md:px-6 py-3 md:py-4 rounded-xl font-bold text-slate-500 uppercase tracking-widest hover:bg-slate-100 transition-colors text-[10px] md:text-xs"
                  >
                      Back
                  </button>
                  <button
                    onClick={handleStart}
                    disabled={loading || !userSide}
                    className="flex-1 bg-yellow-400 text-slate-900 font-black py-3 md:py-4 rounded-xl uppercase tracking-widest hover:bg-yellow-300 disabled:opacity-50 transition-colors flex items-center justify-center shadow-lg shadow-yellow-200 text-xs md:text-sm"
                  >
                    {loading ? (
                        <>
                            <ArrowPathIcon className="w-4 md:w-5 h-4 md:h-5 animate-spin mr-2" />
                            STARTING...
                        </>
                    ) : (
                        <>
                            ENTER SIM <ArrowRightIcon className="w-4 md:w-5 h-4 md:h-5 ml-2" />
                        </>
                    )}
                  </button>
              </div>
          )}
        </div>

      </div>
    </div>
  );
};

// --- NEW: FILTER MODAL FOR REEL VIEW (With Search Bar) ---
const ReelFilterModal = ({ onClose, selectedCategory, selectedRegion, onCategorySelect, onRegionSelect, onClear, searchQuery, onSearchChange }) => {
    return (
        <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in" onClick={onClose}>
            <div className="bg-slate-900 w-full max-w-md rounded-2xl border border-slate-800 shadow-2xl p-4 md:p-6 animate-slide-up" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4 md:mb-6">
                    <h3 className="text-white font-black uppercase tracking-wider text-base md:text-lg flex items-center">
                        <MagnifyingGlassIcon className="w-4 md:w-5 h-4 md:h-5 mr-2 text-yellow-500" /> Filter Feed
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-white"><XMarkIcon className="w-6 h-6"/></button>
                </div>

                {/* SEARCH BAR (Dark Theme) */}
                <div className="mb-4 md:mb-6">
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <MagnifyingGlassIcon className="h-4 md:h-5 w-4 md:w-5 text-slate-500" />
                        </div>
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => onSearchChange(e.target.value)}
                            placeholder="Search topics..."
                            className="block w-full pl-9 md:pl-10 pr-3 py-2.5 md:py-3 border border-slate-700 rounded-xl leading-5 bg-slate-800 text-white placeholder-slate-500 focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 text-xs md:text-sm transition-colors font-medium"
                        />
                    </div>
                </div>

                <div className="space-y-5 md:space-y-6 max-h-[50vh] overflow-y-auto custom-scrollbar">
                    <div>
                        <h4 className="text-[10px] md:text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 md:mb-3">Categories</h4>
                        <div className="flex flex-wrap gap-2">
                            {CATEGORIES.filter(c => c.id !== null).map(cat => {
                                const isActive = selectedCategory === cat.id;
                                const Icon = cat.icon;
                                return (
                                    <button
                                        key={cat.id}
                                        onClick={() => { onCategorySelect(cat.id); onClose(); }}
                                        className={`flex items-center px-2.5 md:px-3 py-1.5 md:py-2 rounded-lg border text-[10px] md:text-xs font-bold uppercase tracking-wider transition-all ${isActive ? 'bg-yellow-500 border-yellow-500 text-slate-900' : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500 hover:text-white'}`}
                                    >
                                        <Icon className="w-3 h-3 mr-1.5" /> {cat.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div>
                        <h4 className="text-[10px] md:text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 md:mb-3">Regions</h4>
                        <div className="flex flex-wrap gap-2">
                            {ALL_REGIONS.slice(0, 12).map(r => {
                                const isActive = selectedRegion === r.id;
                                return (
                                    <button
                                        key={r.id}
                                        onClick={() => { onRegionSelect(r.id); onClose(); }}
                                        className={`flex items-center px-2.5 md:px-3 py-1.5 md:py-2 rounded-lg border text-[10px] md:text-xs font-bold uppercase tracking-wider transition-all ${isActive ? 'bg-blue-600 border-blue-600 text-white' : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500 hover:text-white'}`}
                                    >
                                        {r.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="mt-4 md:mt-6 pt-4 border-t border-slate-800">
                    <button onClick={() => { onClear(); onClose(); }} className="w-full py-2.5 md:py-3 bg-white text-slate-900 rounded-xl font-bold uppercase tracking-widest hover:bg-slate-200 text-xs md:text-sm">
                        Clear All Filters
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- MAIN HOME COMPONENT ---
function Home() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  // Get Menu Context
  const { setIsMenuOpen } = useOutletContext() || {}; 

  const {
    feedTopics,
    loading,
    feedType,
    setFeedType,
    selectedCategory,
    setSelectedCategory,
    selectedRegion,
    setSelectedRegion,
    forceRefresh,
    loadMoreTopics,
    hasMore,
    isFetchingMore
  } = useData();

  const [animatingCategory, setAnimatingCategory] = useState(null);
  const [showLobbyBrowser, setShowLobbyBrowser] = useState(false);
  const [showAiSetup, setShowAiSetup] = useState(false);
  const [showActiveSessions, setShowActiveSessions] = useState(false); 
  
  // NEW STATE FOR REELS
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'reel'
  const [showReelFilter, setShowReelFilter] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(null); // null = all countries (national, 4-country feed)
  const [activeMatches, setActiveMatches] = useState([]);
  const [pinnedIds, setPinnedIds] = useState([]);
  const [hiddenIds, setHiddenIds] = useState([]);

  // PULL-TO-REFRESH STATE
  const [pullY, setPullY] = useState(0);
  const [isPullRefreshing, setIsPullRefreshing] = useState(false);
  const pullStartY = useRef(0);
  const pullStartX = useRef(0);
  const isHorizontalSwipe = useRef(false);
  const PULL_THRESHOLD = 120;

  const fetchActiveMatches = useCallback(async () => {
      if (currentUser) {
          const matches = await getMyActiveMatches(currentUser.uid);
          setActiveMatches(matches);
      }
  }, [currentUser]);

  useEffect(() => {
      fetchActiveMatches();
  }, [fetchActiveMatches]);

  useEffect(() => {
      if (currentUser) {
          setPinnedIds(currentUser.pinnedTopics || []);
          setHiddenIds(currentUser.hiddenTopics || []);
      }
  }, [currentUser]);

  const handlePin = async (id) => {
      if (!currentUser) return;
      const newPins = pinnedIds.includes(id) ? pinnedIds.filter(p => p !== id) : [...pinnedIds, id];
      setPinnedIds(newPins); 
      await saveUserPreference(currentUser.uid, 'pinnedTopics', newPins);
  };

  const handleHide = async (id) => {
      if (!currentUser) return;
      const newHides = [...hiddenIds, id];
      setHiddenIds(newHides); 
      await saveUserPreference(currentUser.uid, 'hiddenTopics', newHides);
  };

  const handleTouchStart = (e) => {
      // Check if touch started inside a horizontal scroll container (widget cards)
      const target = e.target;
      const isInsideHorizontalScroll = target.closest('.overflow-x-auto') || target.closest('[data-horizontal-scroll]');

      if (isInsideHorizontalScroll) {
          isHorizontalSwipe.current = true;
          return; // Don't capture - let the horizontal scroll handle it
      }

      isHorizontalSwipe.current = false;
      pullStartY.current = e.touches[0].clientY;
      pullStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
      // Skip if this is a horizontal swipe in a scroll container
      if (isHorizontalSwipe.current) return;

      const currentY = e.touches[0].clientY;
      const currentX = e.touches[0].clientX;
      const diffY = currentY - pullStartY.current;
      const diffX = currentX - pullStartX.current;

      // If horizontal movement is dominant, mark as horizontal swipe and bail
      if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 10) {
          isHorizontalSwipe.current = true;
          setPullY(0);
          return;
      }

      // Only trigger pull-to-refresh if at top of page and pulling down
      if (window.scrollY === 0 && diffY > 0) {
          const newPullY = Math.min(diffY * 0.4, 160);
          setPullY(newPullY);
      } else {
          setPullY(0);
      }
  };

  const handleTouchEnd = async () => {
      // Reset horizontal swipe flag
      isHorizontalSwipe.current = false;

      if (pullY > PULL_THRESHOLD) {
          setIsPullRefreshing(true);
          setPullY(60);
          try {
             await forceRefresh();
             await new Promise(resolve => setTimeout(resolve, 800));
          } catch (e) { /* refresh error */ }
          setIsPullRefreshing(false);
      }
      setPullY(0);
  };

  const handleCategoryClick = (categoryId) => {
    if (selectedCategory === categoryId && !selectedRegion) return;
    if (feedType !== 'trending') setFeedType('trending');
    setAnimatingCategory(categoryId);
    setTimeout(() => {
      setSelectedCategory(categoryId);
      setSelectedRegion(null);
      setTimeout(() => setAnimatingCategory(null), 300);
    }, 600);
  };

  const handleRegionClick = (regionId) => {
    if (selectedRegion === regionId) return;
    if (feedType !== 'trending') setFeedType('trending');
    const region = ALL_REGIONS.find(r => r.id === regionId);
    setAnimatingCategory(regionId);
    setTimeout(() => {
      setSelectedRegion(regionId);
      setSelectedCategory(null);
      setTimeout(() => setAnimatingCategory(null), 300);
    }, 600);
  };

  const clearFilters = () => {
    handleCategoryClick(null);
    setSearchQuery('');
  };

  const [readingTopic, setReadingTopic] = useState(null);

  // Ads: Pro users never see them; dismissing a slot hides it for the session.
  const isPro = !!currentUser?.isPro;
  const [dismissedAds, setDismissedAds] = useState(() => new Set());

  // Hot Debates rail — fetched independently of the news feed so lively debates surface no
  // matter what the user is browsing, and can never be buried under the stream of new articles.
  const [hotStrip, setHotStrip] = useState([]);
  useEffect(() => {
    let alive = true;
    if (!currentUser) return;
    getHotDebates(12).then(list => { if (alive) setHotStrip(list || []); }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, feedType]);

  // "Local" is STATE news. The user picks a state (ZIP optional — it just resolves to a state).
  // currentUser is a live profile snapshot, so saving homeState updates it reactively.
  const userState = currentUser?.homeState || null;
  const [zipInput, setZipInput] = useState('');
  const [savingState, setSavingState] = useState(false);
  const [stateError, setStateError] = useState('');
  useEffect(() => {
    if (feedType === 'local' && userState) forceRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userState, feedType]);
  const saveState = async (stateCode) => {
    if (!stateCode) return;
    setSavingState(true); setStateError('');
    try {
      await updateUserProfile(currentUser.uid, { homeState: stateCode });
    } catch (e) {
      setStateError('Could not save. Try again.');
    } finally {
      setSavingState(false);
    }
  };
  const saveZipAsState = async () => {
    const z = (zipInput || '').trim();
    if (!/^\d{5}$/.test(z)) { setStateError('Enter a valid 5-digit ZIP, or pick a state.'); return; }
    setSavingState(true); setStateError('');
    try {
      const res = await getLocalNews({ zip: z }); // resolves ZIP → state server-side
      if (res?.stateCode) await updateUserProfile(currentUser.uid, { homeState: res.stateCode });
      else setStateError("Couldn't resolve that ZIP.");
    } catch (e) {
      setStateError('Could not resolve that ZIP. Pick a state instead.');
    } finally {
      setSavingState(false);
    }
  };

  const handleTopicClick = (e, topic) => {
    if (e.target.closest('button') || e.target.closest('a')) return;
    setReadingTopic(topic); // open the in-app reader; the debate CTA lives at the bottom
  };

  const handleCommunityClick = (e, communityId) => {
    e.stopPropagation();
    navigate(`/community/${communityId}`);
  };

  const getRegionName = useCallback((code) => {
    const region = ALL_REGIONS.find(r => r.id === code);
    return region ? region.label : code;
  }, []);

  const filteredItems = React.useMemo(() => {
      const query = searchQuery.toLowerCase().trim();
      const allItems = [...CATEGORIES, ...ALL_REGIONS];
      if (!query) return [...CATEGORIES, ...ALL_REGIONS.filter(r => r.country === 'US').slice(0, 4)];
      return allItems.filter(item => item.label.toLowerCase().includes(query) || (item.id && item.id.toLowerCase().includes(query)));
  }, [searchQuery]);

  // When did a story land — used to keep the news feed newest-first.
  const parseWhen = (t) => {
      const d = t.isoDate ? new Date(t.isoDate).getTime() : 0;
      if (d) return d;
      if (t.createdAt?.toDate) return t.createdAt.toDate().getTime();
      if (t.createdAt?.seconds) return t.createdAt.seconds * 1000;
      return 0;
  };

  const baseList = (feedTopics || [])
      .filter(t => !hiddenIds.includes(t.id))
      .filter(t => {
          if (!searchQuery) return true;
          const q = searchQuery.toLowerCase();
          if (t.title?.toLowerCase().includes(q) || t.summary?.toLowerCase().includes(q) || t.category?.toLowerCase().includes(q)) return true;
          if (t.region?.stateCode?.toLowerCase().includes(q)) return true;
          if (t.region?.countryCode?.toLowerCase().includes(q)) return true;
          if (t.region?.stateCode) {
              const regionName = getRegionName(t.region.stateCode).toLowerCase();
              if (regionName.includes(q)) return true;
          }
          return false;
      });

  // HOT DEBATES — the most-argued topics (hotness-ranked, decayed by age), featured above the
  // news so debate energy stays visible and is never buried by the stream of new articles.
  // Comes from a dedicated query (hotStrip), not just the loaded page. Hidden on the Debates
  // tab (redundant there) and while searching.
  const showHotRail = !searchQuery && (feedType === 'trending' || feedType === 'forYou' || feedType === 'local');
  const hotTopics = showHotRail ? hotStrip.slice(0, 6) : [];
  const hotIds = new Set(hotTopics.map(t => t.id));

  const displayTopics = baseList
      .filter(t => !hotIds.has(t.id)) // featured above; don't repeat in the main list
      .filter(t => feedType === 'timeless' ? true : !t.isEvergreen) // timeless debates live in their own tab
      .filter(t => !selectedCountry || (t.region?.countryCode || 'US') === selectedCountry) // country filter
      .sort((a, b) => {
          const isAPinned = pinnedIds.includes(a.id);
          const isBPinned = pinnedIds.includes(b.id);
          if (isAPinned && !isBPinned) return -1;
          if (!isAPinned && isBPinned) return 1;
          // News-first: freshest stories lead the main feed (hot debates are featured above).
          if (feedType === 'trending' || feedType === 'archive') return parseWhen(b) - parseWhen(a);
          return 0;
      });

  if (!currentUser) return <div className="min-h-screen bg-night p-6 text-center font-mono text-sm uppercase tracking-wider text-ash">Please log in.</div>;

  const AnimatingIcon = animatingCategory
    ? (CATEGORIES.find(c => c.id === animatingCategory)?.icon || ALL_REGIONS.find(r => r.id === animatingCategory)?.icon)
    : (selectedCategory === null && selectedRegion === null && animatingCategory === null ? FireIcon : null);

  // --- REEL VIEW RENDER ---
  if (viewMode === 'reel') {
      return (
          <>
            {/* FULL SCREEN CONTAINER (Fixed z-index to cover Layout) */}
            <div className="fixed inset-0 z-50 bg-black">
                {showReelFilter && (
                    <ReelFilterModal 
                        onClose={() => setShowReelFilter(false)} 
                        selectedCategory={selectedCategory}
                        selectedRegion={selectedRegion}
                        onCategorySelect={handleCategoryClick}
                        onRegionSelect={handleRegionClick}
                        onClear={clearFilters}
                        searchQuery={searchQuery}
                        onSearchChange={setSearchQuery}
                    />
                )}
                
                <NewsReel 
                    topics={displayTopics}
                    onTopicClick={handleTopicClick}
                    onPin={handlePin}
                    onHide={handleHide}
                    pinnedIds={pinnedIds}
                    onMenuClick={() => setIsMenuOpen && setIsMenuOpen(true)}
                    onToggleView={() => setViewMode('list')}
                    onFilterClick={() => setShowReelFilter(true)}
                    onLoadMore={loadMoreTopics}
                    hasMore={hasMore}
                    isFetchingMore={isFetchingMore}
                />
            </div>
          </>
      );
  }

  // --- LIST VIEW RENDER ---
  return (
    <div
        className="flex flex-col min-h-screen relative bg-night text-bone"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
    >
      
      {/* PULL TO REFRESH INDICATOR */}
      <div 
          className="fixed top-16 left-0 right-0 z-40 flex justify-center pointer-events-none transition-transform duration-200 ease-out"
          style={{ transform: `translateY(${pullY - 50}px)`, opacity: pullY > 0 ? 1 : 0 }}
      >
          <div className={`bg-card p-2 rounded-full shadow-lg border border-edge ${isPullRefreshing ? 'animate-spin' : ''}`}>
              <ArrowPathIcon className={`w-6 h-6 ${pullY > PULL_THRESHOLD ? 'text-signal' : 'text-ash'}`} style={{ transform: `rotate(${pullY * 2}deg)` }} />
          </div>
      </div>

      {showLobbyBrowser && <LobbyBrowser onClose={() => setShowLobbyBrowser(false)} />}
      {showAiSetup && <AiSetupModal onClose={() => setShowAiSetup(false)} />}
      {showActiveSessions && <ActiveSessionsModal matches={activeMatches} currentUser={currentUser} onClose={() => setShowActiveSessions(false)} onRefresh={fetchActiveMatches} />}
      {readingTopic && <ArticleReader topic={readingTopic} onClose={() => setReadingTopic(null)} onDebate={(t) => { setReadingTopic(null); navigate(`/debate/${t.id}`); }} />}

      {animatingCategory !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-night/85 backdrop-blur-sm animate-fade-in">
          <div className="animate-bounce-short">
            {AnimatingIcon && <AnimatingIcon className="w-32 h-32 text-signal drop-shadow-2xl" />}
          </div>
        </div>
      )}

      {/* MAIN CONTENT CONTAINER - Pushes down when pulling */}
      <div 
        className="flex-1 transition-transform duration-200 ease-out"
        style={{ transform: `translateY(${pullY > 0 ? pullY * 0.3 : 0}px)` }}
      >

      <div className="p-4 md:p-6 bg-night border-b border-edge">
        {/* STATS HEADER */}
        <div className="grid grid-cols-3 gap-2 md:gap-6 max-w-4xl mx-auto">
          <div className="bg-card border border-edge p-2.5 md:p-4 rounded-[3px] text-center">
            <ShieldCheckIcon className="w-4 md:w-5 h-4 md:h-5 text-signal mx-auto mb-1 md:mb-2" />
            <div className="text-lg md:text-xl font-black text-bone tabular-nums">{currentUser.stats?.repTotal || 0}</div>
            <div className="font-mono text-[8px] md:text-[10px] text-ash tracking-widest uppercase">REP</div>
          </div>
          <div className="bg-card border border-edge p-2.5 md:p-4 rounded-[3px] text-center">
            <FireIcon className="w-4 md:w-5 h-4 md:h-5 text-signal mx-auto mb-1 md:mb-2" />
            <div className="text-lg md:text-xl font-black text-bone tabular-nums">{currentUser.stats?.winStreak || 0}</div>
            <div className="font-mono text-[8px] md:text-[10px] text-ash tracking-widest uppercase">STREAK</div>
          </div>
          <div className="bg-card border border-edge p-2.5 md:p-4 rounded-[3px] text-center">
            <div className="w-6 md:w-8 h-6 md:h-8 mx-auto mb-1 bg-signal rounded-full flex items-center justify-center text-white font-black text-xs md:text-sm">
              {currentUser.stats?.level || 1}
            </div>
            <div className="text-lg md:text-xl font-black text-bone mt-1 tabular-nums">{currentUser.stats?.xp || 0} XP</div>
            <div className="font-mono text-[8px] md:text-[10px] text-ash tracking-widest uppercase">LEVEL</div>
          </div>
        </div>
      </div>

      <div className="flex-1 p-4 md:p-8 max-w-4xl mx-auto w-full overflow-hidden">

        {/* --- VIEW MODE TOGGLE --- */}
        <div className="flex justify-end mb-4">
            <div className="bg-card border border-edge p-1 rounded-[3px] flex gap-1">
                <button onClick={() => setViewMode('list')} className={`p-2 rounded-[2px] transition-all ${viewMode === 'list' ? 'bg-edge text-bone' : 'text-ash hover:text-bone'}`} title="List View"><Squares2X2Icon className="w-5 h-5" /></button>
                <button onClick={() => setViewMode('reel')} className={`p-2 rounded-[2px] transition-all ${viewMode === 'reel' ? 'bg-edge text-bone' : 'text-ash hover:text-bone'}`} title="Reel View"><ReelIcon className="w-5 h-5" /></button>
            </div>
        </div>

        {/* WIDGETS (List Mode Only) */}
        <div className="flex space-x-3 md:space-x-4 overflow-x-auto pb-4 mb-4 md:mb-6 hide-scrollbar -mx-4 px-4 md:mx-0 md:px-0">
            <button onClick={() => setShowActiveSessions(true)} className="min-w-[65%] md:min-w-[45%] flex-shrink-0 relative overflow-hidden rounded-[3px] p-4 md:p-6 text-left transition-all duration-200 hover:-translate-y-0.5 active:scale-95 bg-card border border-edge text-bone hover:border-ash/50 group"><div className="relative z-10"><BoltIcon className={`w-6 md:w-8 h-6 md:h-8 mb-3 md:mb-4 group-hover:rotate-12 transition-transform ${activeMatches.length > 0 ? 'text-signal animate-pulse' : 'text-ash/50'}`} /><h3 className="font-serif text-base md:text-xl font-bold tracking-tight">Active Sessions</h3><p className="font-mono text-ash text-[10px] md:text-xs mt-1 uppercase tracking-wider">{activeMatches.length} live pending</p></div><div className="absolute right-0 bottom-0 opacity-[0.04]"><BoltIcon className="w-24 md:w-32 h-24 md:h-32 -mb-8 -mr-8 text-bone" /></div></button>
            <button onClick={() => navigate('/arena10v1-lobby')} className="min-w-[65%] md:min-w-[45%] flex-shrink-0 relative overflow-hidden rounded-[3px] p-4 md:p-6 text-left transition-all duration-200 hover:-translate-y-0.5 active:scale-95 bg-signal text-white group"><div className="absolute top-0 left-0 right-0 h-[3px] bg-white/25" /><div className="relative z-10"><FireIcon className="w-6 md:w-8 h-6 md:h-8 text-white mb-3 md:mb-4 group-hover:rotate-12 transition-transform" /><h3 className="font-serif text-base md:text-xl font-bold tracking-tight">10v1 Arena</h3><p className="font-mono text-white/80 text-[10px] md:text-xs mt-1 uppercase tracking-wider">1 vs 10 Debates</p></div><div className="absolute right-0 bottom-0 opacity-10"><FireIcon className="w-24 md:w-32 h-24 md:h-32 -mb-8 -mr-8 text-white" /></div></button>
            <button onClick={() => setShowAiSetup(true)} className="min-w-[65%] md:min-w-[45%] flex-shrink-0 relative overflow-hidden rounded-[3px] p-4 md:p-6 text-left transition-all duration-200 hover:-translate-y-0.5 active:scale-95 bg-card border border-edge text-bone hover:border-ash/50 group"><div className="relative z-10"><CpuChipIcon className="w-6 md:w-8 h-6 md:h-8 text-ash mb-3 md:mb-4 group-hover:rotate-12 transition-transform" /><h3 className="font-serif text-base md:text-xl font-bold tracking-tight">Training Sim</h3><p className="font-mono text-ash text-[10px] md:text-xs mt-1 uppercase tracking-wider">Vs AI Opponent</p></div><div className="absolute right-0 bottom-0 opacity-[0.04]"><CpuChipIcon className="w-24 md:w-32 h-24 md:h-32 -mb-8 -mr-8 text-bone" /></div></button>
            <button onClick={() => setShowLobbyBrowser(true)} className="min-w-[65%] md:min-w-[45%] flex-shrink-0 relative overflow-hidden rounded-[3px] p-4 md:p-6 text-left transition-all duration-200 hover:-translate-y-0.5 active:scale-95 bg-card border border-edge text-bone hover:border-ash/50 group"><div className="relative z-10"><UserGroupIcon className="w-6 md:w-8 h-6 md:h-8 text-ash mb-3 md:mb-4 group-hover:scale-110 transition-transform" /><h3 className="font-serif text-base md:text-xl font-bold tracking-tight">Arena</h3><p className="font-mono text-ash text-[10px] md:text-xs mt-1 uppercase tracking-wider">Browse Lobbies</p></div><div className="absolute right-0 bottom-0 opacity-[0.04]"><UserGroupIcon className="w-24 md:w-32 h-24 md:h-32 -mb-8 -mr-8 text-bone" /></div></button>
        </div>

        {/* Feed Type Tabs */}
        <div className="flex space-x-4 md:space-x-6 mb-4 md:mb-6 border-b border-edge overflow-x-auto hide-scrollbar">
          <button onClick={() => setFeedType('trending')} className={`pb-2 md:pb-3 px-0.5 font-mono text-[11px] md:text-xs font-semibold uppercase tracking-widest transition-colors relative whitespace-nowrap ${feedType === 'trending' ? 'text-bone' : 'text-ash hover:text-bone'}`}>News{feedType === 'trending' && <div className="absolute -bottom-px left-0 right-0 h-0.5 bg-signal"></div>}</button>
          <button onClick={() => setFeedType('debates')} className={`pb-2 md:pb-3 px-0.5 font-mono text-[11px] md:text-xs font-semibold uppercase tracking-widest transition-colors relative whitespace-nowrap flex items-center ${feedType === 'debates' ? 'text-bone' : 'text-ash hover:text-bone'}`}><FireIcon className="w-3.5 md:w-4 h-3.5 md:h-4 mr-1 md:mr-1.5" />Debate{feedType === 'debates' && <div className="absolute -bottom-px left-0 right-0 h-0.5 bg-signal"></div>}</button>
          <button onClick={() => setFeedType('timeless')} className={`pb-2 md:pb-3 px-0.5 font-mono text-[11px] md:text-xs font-semibold uppercase tracking-widest transition-colors relative whitespace-nowrap flex items-center ${feedType === 'timeless' ? 'text-bone' : 'text-ash hover:text-bone'}`}><SparklesIcon className="w-3.5 md:w-4 h-3.5 md:h-4 mr-1 md:mr-1.5" />Timeless{feedType === 'timeless' && <div className="absolute -bottom-px left-0 right-0 h-0.5 bg-signal"></div>}</button>
          <button onClick={() => setFeedType('forYou')} className={`pb-2 md:pb-3 px-0.5 font-mono text-[11px] md:text-xs font-semibold uppercase tracking-widest transition-colors relative whitespace-nowrap flex items-center ${feedType === 'forYou' ? 'text-bone' : 'text-ash hover:text-bone'}`}><SparklesIcon className="w-3.5 md:w-4 h-3.5 md:h-4 mr-1 md:mr-1.5" />For You{feedType === 'forYou' && <div className="absolute -bottom-px left-0 right-0 h-0.5 bg-signal"></div>}</button>
          <button onClick={() => setFeedType('following')} className={`pb-2 md:pb-3 px-0.5 font-mono text-[11px] md:text-xs font-semibold uppercase tracking-widest transition-colors relative whitespace-nowrap flex items-center ${feedType === 'following' ? 'text-bone' : 'text-ash hover:text-bone'}`}><UsersIcon className="w-3.5 md:w-4 h-3.5 md:h-4 mr-1 md:mr-1.5" />Following{feedType === 'following' && <div className="absolute -bottom-px left-0 right-0 h-0.5 bg-signal"></div>}</button>
          <button onClick={() => setFeedType('archive')} className={`pb-2 md:pb-3 px-0.5 font-mono text-[11px] md:text-xs font-semibold uppercase tracking-widest transition-colors relative whitespace-nowrap flex items-center ${feedType === 'archive' ? 'text-bone' : 'text-ash hover:text-bone'}`}><ArchiveBoxIcon className="w-3.5 md:w-4 h-3.5 md:h-4 mr-1 md:mr-1.5" />Archive{feedType === 'archive' && <div className="absolute -bottom-px left-0 right-0 h-0.5 bg-signal"></div>}</button>
        </div>

        {/* Search Filter Bar */}
        {(feedType === 'trending' || feedType === 'archive') && (
          <div className="mb-6 md:mb-8">
            {/* Enhanced Search Bar */}
            <div className="relative mb-3 md:mb-4">
              <div className="absolute inset-y-0 left-0 pl-3 md:pl-4 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-4 md:h-5 w-4 md:w-5 text-ash" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search topics, categories, or regions..."
                className="block w-full pl-10 md:pl-12 pr-4 py-3 md:py-3.5 border border-edge rounded-[3px] leading-5 bg-card text-bone placeholder-ash focus:outline-none focus:border-signal focus:ring-1 focus:ring-signal text-xs md:text-sm transition-all font-mono"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-ash hover:text-bone transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>

            {/* Filter Categories Section */}
            {!searchQuery && (
              <div className="mb-3 md:mb-4">
                <h4 className="font-mono text-[10px] md:text-xs font-semibold text-ash uppercase tracking-widest mb-2 md:mb-3 px-1">Filter by Category</h4>
                <div className="flex overflow-x-auto pb-3 md:pb-4 hide-scrollbar space-x-2 -mx-4 px-4 md:mx-0 md:px-0">
                  {CATEGORIES.filter(c => c.id !== null).map(cat => {
                    const Icon = cat.icon;
                    const isActive = selectedCategory === cat.id && !selectedRegion;
                    return (
                      <button
                        key={cat.id}
                        onClick={() => handleCategoryClick(cat.id)}
                        className={`flex items-center flex-shrink-0 px-3 md:px-4 py-2 md:py-2.5 rounded-[3px] font-bold text-[10px] md:text-xs uppercase tracking-wider transition-all duration-200 ${isActive ? 'bg-signal text-white scale-105' : 'bg-card border border-edge text-ash hover:border-ash/60'}`}
                      >
                        <Icon className={`w-3.5 md:w-4 h-3.5 md:h-4 mr-1.5 md:mr-2 ${isActive ? 'text-white' : 'text-ash'}`} />
                        {cat.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Filter Regions Section */}
            {!searchQuery && (
              <div>
                <h4 className="font-mono text-[10px] md:text-xs font-semibold text-ash uppercase tracking-widest mb-2 md:mb-3 px-1">Filter by Region</h4>
                <div className="flex overflow-x-auto pb-3 md:pb-4 hide-scrollbar space-x-2 -mx-4 px-4 md:mx-0 md:px-0">
                  {ALL_REGIONS.filter(r => r.country === 'US').slice(0, 10).map(region => {
                    const Icon = region.icon;
                    const isActive = selectedRegion === region.id;
                    return (
                      <button
                        key={region.id}
                        onClick={() => handleRegionClick(region.id)}
                        className={`flex items-center flex-shrink-0 px-3 md:px-4 py-2 md:py-2.5 rounded-[3px] font-bold text-[10px] md:text-xs uppercase tracking-wider transition-all duration-200 ${isActive ? 'bg-signal text-white scale-105' : 'bg-card border border-edge text-ash hover:border-ash/60'}`}
                      >
                        <Icon className={`w-3.5 md:w-4 h-3.5 md:h-4 mr-1.5 md:mr-2 ${isActive ? 'text-white' : 'text-ash'}`} />
                        {region.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Search Results Display */}
            {searchQuery && (
              <div className="flex overflow-x-auto pb-3 md:pb-4 hide-scrollbar space-x-2 -mx-4 px-4 md:mx-0 md:px-0">
                {filteredItems.length > 0 ? (
                  filteredItems.map(item => {
                    const Icon = item.icon;
                    const isActive = item.type === 'category' ? selectedCategory === item.id && !selectedRegion : selectedRegion === item.id;
                    const uniqueKey = item.type === 'region' ? `${item.country}-${item.id}` : item.id;
                    return (
                      <button
                        key={uniqueKey}
                        onClick={() => item.type === 'category' ? handleCategoryClick(item.id) : handleRegionClick(item.id)}
                        className={`flex items-center flex-shrink-0 px-3 md:px-4 py-2 md:py-2.5 rounded-[3px] font-bold text-[10px] md:text-xs uppercase tracking-wider transition-all duration-200 ${isActive ? 'bg-signal text-white scale-105' : 'bg-card border border-edge text-ash hover:border-ash/60'}`}
                      >
                        <Icon className={`w-3.5 md:w-4 h-3.5 md:h-4 mr-1.5 md:mr-2 ${isActive ? 'text-white' : 'text-ash'}`} />
                        {item.label}
                      </button>
                    );
                  })
                ) : (
                  <div className="font-mono text-xs md:text-sm text-ash py-2 px-1">No matching filters found</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* HOT DEBATES — featured strip of the most-argued topics right now */}
        {hotTopics.length > 0 && (
          <div className="mb-6 md:mb-8">
            <div className="flex items-center gap-2 mb-2.5 pb-1.5 border-b border-edge">
              <FireIcon className="w-3.5 h-3.5 text-signal" />
              <h4 className="font-mono text-[11px] font-semibold text-signal uppercase tracking-widest">Hot Debates</h4>
              <span className="font-mono text-[10px] text-ash normal-case tracking-normal">· most argued right now</span>
            </div>
            <div className="flex gap-3 overflow-x-auto hide-scrollbar -mx-4 px-4 md:mx-0 md:px-0 pb-1">
              {hotTopics.map(t => {
                const cA = t.sideCounts?.A || 0, cB = t.sideCounts?.B || 0, tot = cA + cB;
                const aPct = tot ? Math.round((cA / tot) * 100) : 50;
                const q = t.debateQuestion || t.title;
                return (
                  <button key={t.id} onClick={() => navigate(`/debate/${t.id}`)} className="min-w-[80%] sm:min-w-[47%] md:min-w-[32%] flex-shrink-0 text-left bg-card border border-signal/40 rounded-[3px] p-4 hover:border-signal transition-colors relative overflow-hidden group">
                    <div className="absolute top-0 left-0 right-0 h-[3px] bg-signal" />
                    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-signal mb-2">
                      <FireIcon className="w-3 h-3" /> {t.postCount} arguing
                      {t.category && <span className="text-ash">· {t.category}</span>}
                    </div>
                    <h5 className="font-serif text-base md:text-lg font-bold text-bone leading-snug mb-3 line-clamp-3" style={{ textWrap: 'balance' }}>{q}</h5>
                    <div className="h-1.5 rounded-full bg-edge overflow-hidden flex mb-1.5">
                      <div className="h-full bg-bone" style={{ width: `${aPct}%` }} />
                      <div className="h-full bg-signal" style={{ width: `${100 - aPct}%` }} />
                    </div>
                    <div className="flex justify-between font-mono text-[10px] text-ash">
                      <span>{t.sides?.[0]?.label || 'For'} {aPct}%</span>
                      <span>{100 - aPct}% {t.sides?.[1]?.label || 'Against'}</span>
                    </div>
                    <div className="mt-3 inline-flex items-center font-mono text-[10px] uppercase tracking-widest text-signal font-semibold">Join the debate <ArrowRightIcon className="w-3 h-3 ml-1 group-hover:translate-x-0.5 transition-transform" /></div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Country filter — national news across the four countries */}
        {!searchQuery && ['trending', 'debates', 'forYou'].includes(feedType) && (
          <div className="flex gap-2 mb-4 overflow-x-auto hide-scrollbar -mx-4 px-4 md:mx-0 md:px-0">
            {[{ id: null, label: 'All' }, { id: 'US', label: '🇺🇸 US' }, { id: 'CA', label: '🇨🇦 Canada' }, { id: 'UK', label: '🇬🇧 UK' }, { id: 'AU', label: '🇦🇺 Australia' }].map(c => {
              const active = selectedCountry === c.id;
              return <button key={c.label} onClick={() => setSelectedCountry(c.id)} className={`flex-shrink-0 px-3 py-1.5 rounded-[3px] font-mono text-[10px] md:text-xs font-semibold uppercase tracking-wider transition-all ${active ? 'bg-signal text-white' : 'bg-card border border-edge text-ash hover:border-ash/60'}`}>{c.label}</button>;
            })}
          </div>
        )}

        <div className="flex items-center justify-between mb-3 md:mb-4">
          <h3 className="font-serif text-base md:text-lg font-bold text-bone flex items-center tracking-tight">{feedType === 'local' ? (<><MapPinIcon className="w-4 h-4 mr-1.5 text-signal" />{userState ? `${ALL_REGIONS.find(r => r.id === userState)?.label || userState}` : 'Local News'}{userState && <button onClick={() => updateUserProfile(currentUser.uid, { homeState: '' })} className="ml-2 text-[10px] font-mono uppercase tracking-widest text-ash hover:text-signal">change</button>}</>) : feedType === 'timeless' ? (<><SparklesIcon className="w-4 h-4 mr-1.5 text-signal" />Timeless Debates</>) : feedType === 'debates' ? 'Hottest Debates' : (feedType === 'forYou' ? 'Recommended for You' : (feedType === 'following' ? 'From People You Follow' : (feedType === 'archive' ? 'Archived' : (selectedCategory ? `${selectedCategory}` : (selectedRegion ? `${ALL_REGIONS.find(r => r.id === selectedRegion)?.label}` : 'Top Stories')))))}</h3>
          {(selectedCategory || selectedRegion || searchQuery) && (feedType === 'trending' || feedType === 'archive') && (<button onClick={clearFilters} className="text-[10px] md:text-xs font-bold text-red-500 hover:text-red-700 uppercase tracking-wider flex items-center"><XMarkIcon className="w-3.5 md:w-4 h-3.5 md:h-4 mr-1" /> Clear</button>)}
          {hiddenIds.length > 0 && (<button onClick={() => { setHiddenIds([]); saveUserPreference(currentUser.uid, 'hiddenTopics', []); }} className="text-[10px] md:text-xs font-bold text-blue-500 hover:underline uppercase tracking-wider flex items-center">Reset ({hiddenIds.length})</button>)}
        </div>

        {/* Topic Feed */}
        <div className="space-y-2 md:space-y-3 pb-20 md:pb-0">
          {feedType === 'local' && !userState ? (
            <div className="p-6 md:p-8 text-center bg-card rounded-[3px] border border-edge">
              <MapPinIcon className="w-8 md:w-10 h-8 md:h-10 text-signal mx-auto mb-3" />
              <h4 className="font-serif text-lg md:text-xl font-bold text-bone mb-1">News from your state</h4>
              <p className="text-ash text-xs md:text-sm font-mono mb-5">Choose your state to see its news.</p>
              <div className="max-w-xs mx-auto">
                <select
                  value=""
                  onChange={(e) => saveState(e.target.value)}
                  disabled={savingState}
                  className="w-full bg-night border border-edge rounded-[3px] px-3 py-2.5 text-bone font-mono focus:outline-none focus:border-signal disabled:opacity-50"
                >
                  <option value="" disabled>Select a state…</option>
                  {ALL_REGIONS.filter(r => r.country === 'US').map(r => (
                    <option key={r.id} value={r.id}>{r.label}</option>
                  ))}
                </select>
                <div className="flex items-center gap-2 my-3"><div className="flex-1 h-px bg-edge" /><span className="font-mono text-[10px] text-ash uppercase tracking-widest">or ZIP</span><div className="flex-1 h-px bg-edge" /></div>
                <div className="flex gap-2">
                  <input
                    value={zipInput}
                    onChange={(e) => { setZipInput(e.target.value.replace(/\D/g, '').slice(0, 5)); setStateError(''); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveZipAsState(); }}
                    inputMode="numeric"
                    placeholder="e.g. 78701"
                    className="flex-1 bg-night border border-edge rounded-[3px] px-3 py-2.5 text-bone font-mono placeholder-ash text-center tracking-widest focus:outline-none focus:border-signal"
                  />
                  <button onClick={saveZipAsState} disabled={savingState} className="bg-signal text-white font-bold px-4 rounded-[3px] font-mono text-xs uppercase tracking-wider hover:brightness-110 disabled:opacity-50">{savingState ? '…' : 'Go'}</button>
                </div>
              </div>
              {stateError && <p className="text-signal text-[11px] font-mono mt-2">{stateError}</p>}
              <p className="text-ash/60 text-[10px] font-mono mt-4 uppercase tracking-widest">A ZIP just picks its state — state news is what's shown</p>
            </div>
          ) : loading ? <div className="text-ash text-xs md:text-sm font-mono uppercase tracking-wider p-4 text-center animate-pulse flex flex-col items-center"><SparklesIcon className="w-5 md:w-6 h-5 md:h-6 text-signal mb-2 animate-spin-slow" />{feedType === 'local' ? 'Pulling your local news…' : 'Loading the latest…'}</div> : displayTopics.length === 0 ? <div className="p-6 md:p-8 text-center bg-card rounded-[3px] border border-dashed border-edge"><FlagIcon className="w-8 md:w-10 h-8 md:h-10 text-ash/50 mx-auto mb-3" /><p className="text-ash font-medium text-sm md:text-base">{feedType === 'archive' ? "No archived stories found." : feedType === 'debates' ? "No debates are hot yet." : feedType === 'timeless' ? "No timeless debates yet." : feedType === 'local' ? `No news for ${ALL_REGIONS.find(r => r.id === userState)?.label || userState} yet — it fills in as the feed pulls state sources.` : "No matching stories found."}</p>{feedType === 'forYou' ? <p className="text-xs md:text-sm mt-2 text-ash">Join a few debates to get better recommendations!</p> : feedType === 'following' ? <p className="text-xs md:text-sm mt-2 text-ash">Follow more users to see their activity here.</p> : feedType === 'debates' ? <button onClick={() => setFeedType('trending')} className="mt-4 text-xs md:text-sm font-bold text-bone hover:underline">Open a story & start the first argument →</button> : feedType === 'local' ? <button onClick={() => updateUserProfile(currentUser.uid, { homeState: '' })} className="mt-4 text-xs md:text-sm font-bold text-bone hover:underline">Change state</button> : feedType === 'archive' ? <button onClick={() => setFeedType('trending')} className="mt-4 text-xs md:text-sm font-bold text-bone hover:underline">View Top Stories</button> : <button onClick={clearFilters} className="mt-4 text-xs md:text-sm font-bold text-bone hover:underline">View All Stories</button>}</div> : (
            <>
                {(() => {
                    const card = (topic) => (
                        <HomeTopicCard key={topic.id} topic={topic} onTopicClick={handleTopicClick} onCommunityClick={handleCommunityClick} isPinned={pinnedIds.includes(topic.id)} onPin={handlePin} onHide={handleHide} showQuestion={feedType === 'debates'} />
                    );
                    // Interleave sparse, dismissible ad slots into news-style feeds (never for Pro).
                    const showAds = ADS_ENABLED && !isPro && ['trending', 'forYou', 'local', 'archive'].includes(feedType);
                    const renderFeed = (list) => {
                        const out = [];
                        list.forEach((t, i) => {
                            out.push(card(t));
                            if (showAds && (i + 1) % AD_FREQUENCY === 0 && i < list.length - 1) {
                                const slotKey = `ad_${feedType}_${i}`;
                                if (!dismissedAds.has(slotKey)) {
                                    out.push(<FeedAdSlot key={slotKey} index={Math.floor(i / AD_FREQUENCY)} navigate={navigate} onDismiss={() => setDismissedAds(prev => { const n = new Set(prev); n.add(slotKey); return n; })} />);
                                }
                            }
                        });
                        return out;
                    };
                    const news = displayTopics.filter(t => !t.isEvergreen);
                    const timeless = displayTopics.filter(t => t.isEvergreen);
                    // Split into News vs Timeless only when both are present; otherwise a flat list.
                    // On the Debates tab it's already one engagement-ranked list — never split.
                    if (feedType !== 'debates' && news.length > 0 && timeless.length > 0) {
                        return (
                            <>
                                <div className="flex items-center gap-2 mb-2 mt-1 pb-1.5 border-b border-edge">
                                    <NewspaperIcon className="w-3.5 h-3.5 text-ash" />
                                    <h4 className="font-mono text-[11px] font-semibold text-bone uppercase tracking-widest">Today's News</h4>
                                </div>
                                {renderFeed(news)}
                                <div className="flex items-center gap-2 mb-2 mt-7 pb-1.5 border-b border-edge">
                                    <SparklesIcon className="w-3.5 h-3.5 text-signal" />
                                    <h4 className="font-mono text-[11px] font-semibold text-signal uppercase tracking-widest">Timeless Debates</h4>
                                </div>
                                {timeless.map(card)}
                            </>
                        );
                    }
                    return renderFeed(displayTopics);
                })()}
                {hasMore && !searchQuery && (<div className="flex justify-center mt-5 md:mt-6 mb-12"><button onClick={loadMoreTopics} disabled={isFetchingMore} className="bg-card border border-edge text-bone font-mono py-2.5 md:py-3 px-6 md:px-8 rounded-[3px] flex items-center hover:border-ash/60 transition-all active:scale-95 disabled:opacity-50 text-[10px] md:text-xs uppercase tracking-widest">{isFetchingMore ? <ArrowPathIcon className="w-3.5 md:w-4 h-3.5 md:h-4 animate-spin mr-2"/> : <ArrowDownCircleIcon className="w-3.5 md:w-4 h-3.5 md:h-4 mr-2"/>}{isFetchingMore ? 'Fetching...' : 'Show More'}</button></div>)}
            </>
          )}
        </div>

      </div>
      </div>
    </div>
  );
}

export default Home;